import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy, addDoc, Timestamp, deleteDoc, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType, withRetry, processPayrollWithTransaction, updateEmployeePayrollMarkedDeductions } from '../firebase';
import { handleAsyncAction } from '../utils/AsyncActionHandler';
import { Employee, DeductionCategory, Payroll, PayrollDeduction, ComplianceRecord } from '../types';
import { EMPLOYEE_CATEGORIES, getEmployeeCategory } from '../components/EmployeeCategorySelector';
import { useAuth } from '../App';
import SuccessPopup from '../components/SuccessPopup';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { 
  Users, 
  CreditCard, 
  Plus, 
  Trash2, 
  Edit2, 
  X, 
  Loader2, 
  Search,
  Filter,
  DollarSign,
  Briefcase,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Mail,
  Eye,
  MapPin,
  Clock,
  LogIn,
  LogOut,
  Download,
  Navigation,
  Laptop,
  Calculator,
  FileText,
  BarChart3,
  TrendingUp,
  ShieldCheck,
  FileSpreadsheet,
  HeartPulse,
  Award,
  FilePlus,
  Landmark,
  CheckSquare,
  Square,
  AlertTriangle,
  Layers,
  ChevronRight,
  Upload,
  UploadCloud,
  FileUp,
  RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { GenerateReportTrigger } from '../components/GenerateReportTrigger';
import { toast } from 'sonner';
import { downloadFile } from '../lib/downloadHelper';
import ConfirmationModal from '../components/ConfirmationModal';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const isValidEmail = (email?: string) => {
  if (!email) return false;
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
};
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';

interface BulkComplianceRowPreview {
  rowIndex: number;
  rawIdentifier: string;
  employeeNameInput: string;
  matchedEmployee?: Employee;
  matchError?: string;
  nssfNumber?: string;
  nhifNumber?: string;
  wcfNumber?: string;
  oshaRegistrationNo?: string;
  oshaMedicalExamDate?: string;
  oshaStatus?: 'compliant' | 'pending_exam' | 'expired';
  heslbControlNo?: string;
  heslbBalance?: number;
  claimAuthority?: 'WCF' | 'OSHA' | 'NHIF' | 'NSSF' | 'SDL' | 'HESLB' | 'General';
  claimTitle?: string;
  claimRef?: string;
  claimAmount?: number;
  claimStatus?: 'compliant' | 'submitted' | 'under_review' | 'approved' | 'paid' | 'expired';
  incidentDate?: string;
  notes?: string;
  selected: boolean;
}

interface ParsedEmployeeRow {
  rowIndex: number;
  employeeCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  department: string;
  position: string;
  category: string;
  salary: number;
  tin: string;
  nssfNumber: string;
  nhifNumber: string;
  wcfNumber: string;
  bankName: string;
  accountNumber: string;
  joiningDate: string;
  status: 'active' | 'inactive';
  hasHeslbLoan: boolean;
  heslbControlNo: string;
  heslbBalance: number;
  oshaRegistrationNo: string;
  oshaMedicalExamDate: string;
  oshaStatus: 'compliant' | 'pending_exam' | 'expired' | 'exempt';
  selected: boolean;
  validationErrors: string[];
}

const HRDashboard: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [deductionCategories, setDeductionCategories] = useState<DeductionCategory[]>([]);
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [complianceRecords, setComplianceRecords] = useState<ComplianceRecord[]>([]);
  const [attendanceLogs, setAttendanceLogs] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'employees' | 'payroll' | 'deductions' | 'attendance' | 'compliance'>('employees');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [salaryRangeFilter, setSalaryRangeFilter] = useState<'all' | 'under1m' | '1m_to_3m' | 'above3m'>('all');
  
  // Clock state for real-time visual feedback
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Attendance Form and Filters
  const [attendanceEmployeeId, setAttendanceEmployeeId] = useState<string>('');
  const [attendanceNote, setAttendanceNote] = useState<string>('');
  const [attendanceSearchTerm, setAttendanceSearchTerm] = useState('');
  const [attendanceFilterType, setAttendanceFilterType] = useState<'all' | 'check-in' | 'check-out'>('all');
  const [attendanceFilterDate, setAttendanceFilterDate] = useState<string>('');
  
  // Geolocation States
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [geoCoords, setGeoCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  
  // Modals & Export Options
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [isDeductionModalOpen, setIsDeductionModalOpen] = useState(false);
  const [isPayrollModalOpen, setIsPayrollModalOpen] = useState(false);
  const [isPayrollDetailsModalOpen, setIsPayrollDetailsModalOpen] = useState(false);
  const [isComplianceModalOpen, setIsComplianceModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [selectedBatchMonths, setSelectedBatchMonths] = useState<string[]>([]);
  const [selectedPayrollIds, setSelectedPayrollIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Compliance Filters & Bulk Upload States
  const [complianceSearch, setComplianceSearch] = useState('');
  const [complianceAuthorityFilter, setComplianceAuthorityFilter] = useState<'all' | 'WCF' | 'OSHA' | 'NHIF' | 'NSSF' | 'SDL' | 'HESLB'>('all');
  const [isBulkComplianceModalOpen, setIsBulkComplianceModalOpen] = useState(false);
  const [bulkComplianceRows, setBulkComplianceRows] = useState<BulkComplianceRowPreview[]>([]);
  const [bulkComplianceFileName, setBulkComplianceFileName] = useState<string>('');
  const [isBulkUploading, setIsBulkUploading] = useState<boolean>(false);
  const [bulkUploadSummary, setBulkUploadSummary] = useState<{ updatedProfiles: number; createdClaims: number; failedRows: number } | null>(null);

  // Employee Excel Import States
  const [isImportEmployeeModalOpen, setIsImportEmployeeModalOpen] = useState<boolean>(false);
  const [importedEmployeeRows, setImportedEmployeeRows] = useState<ParsedEmployeeRow[]>([]);
  const [employeeExcelFileName, setEmployeeExcelFileName] = useState<string>('');
  const [isImportingEmployees, setIsImportingEmployees] = useState<boolean>(false);
  const employeeFileInputRef = React.useRef<HTMLInputElement>(null);

  // Dynamic Export Options
  const [exportOpts, setExportOpts] = useState({
    includeBasicSalary: true,
    includeAllowances: true,
    includeGrossPay: true,
    includePAYE: true,
    includeNSSFEmployee: true,
    includeNHIFEmployee: true,
    includeHESLB: true,
    includeLoansAndOtherDeductions: true,
    includeTotalDeductions: true,
    includeNetSalary: true,
    includeNSSFEmployer: true,
    includeSDL: true,
    includeWCF: true,
    includeNHIFEmployer: true,
    includeTotalEmployer: true,
    includeCompanyCost: true,
    includeBankDetails: true
  });
  
  // Form States
  const [employeeForm, setEmployeeForm] = useState({
    employeeCode: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    category: 'Technician',
    salary: 0,
    tin: '',
    nssfNumber: '',
    nhifNumber: '',
    wcfNumber: '',
    oshaRegistrationNo: '',
    oshaMedicalExamDate: '',
    oshaStatus: 'compliant' as 'compliant' | 'pending_exam' | 'expired' | 'exempt',
    heslbControlNo: '',
    heslbBalance: 0,
    bankName: '',
    accountNumber: '',
    joiningDate: new Date().toISOString().split('T')[0],
    status: 'active' as 'active' | 'inactive',
    hasHeslbLoan: false,
    taxResidency: 'resident' as 'resident' | 'non-resident',
    isTaxExempt: false,
    allowances: [] as { name: string; amount: number }[]
  });

  const [deductionForm, setDeductionForm] = useState({
    name: '',
    description: '',
    type: 'fixed' as 'fixed' | 'percentage',
    value: 0,
    categoryKind: 'loan' as 'general' | 'loan' | 'advance' | 'union' | 'loss_recovery',
    totalPrincipal: 0,
    totalReturn: 0,
    remainingBalance: 0,
    monthlyInstallment: 0,
    loanDate: new Date().toISOString().split('T')[0],
    durationMonths: 12,
    interestRate: 0,
    monthsPaid: 0,
    assignedEmployeeIds: [] as string[]
  });

  const [complianceForm, setComplianceForm] = useState({
    employeeId: '',
    authority: 'WCF' as 'WCF' | 'OSHA' | 'NHIF' | 'NSSF' | 'SDL' | 'HESLB' | 'General',
    title: '',
    referenceNumber: '',
    claimAmount: 0,
    status: 'submitted' as 'compliant' | 'submitted' | 'under_review' | 'approved' | 'paid' | 'expired',
    incidentOrEventDate: new Date().toISOString().split('T')[0],
    expiryOrDueDate: '',
    notes: ''
  });

  const [payrollForm, setPayrollForm] = useState({
    employeeIds: [] as string[],
    month: format(new Date(), 'yyyy-MM'),
    deductions: [] as PayrollDeduction[],
    employeeCustomDeductions: {} as Record<string, string[]>,
    employeeTaxResidency: {} as Record<string, 'resident' | 'non-resident'>,
    employeeTaxExempt: {} as Record<string, boolean>,
    employeeStatutoryOpt: {} as Record<string, { nssf: boolean; nhif: boolean; paye: boolean; heslb: boolean }>
  });

  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [editingDeduction, setEditingDeduction] = useState<DeductionCategory | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [isMcColnCalculatorOpen, setIsMcColnCalculatorOpen] = useState(false);
  const [calcBasicSalary, setCalcBasicSalary] = useState<number>(1500000);
  const [calcAllowances, setCalcAllowances] = useState<number>(200000);
  const [calcHasHeslb, setCalcHasHeslb] = useState<boolean>(false);
  const { user, isAdmin, isHR, isHOD, profile } = useAuth();

  const canManageHR = isAdmin || isHR || isHOD;

  const logAudit = async (action: string, details: string, batchMonth: string) => {
    try {
      await addDoc(collection(db, 'payrollAuditLogs'), {
        adminUserId: user?.uid || profile?.uid || 'admin',
        adminEmail: user?.email || profile?.email || 'admin@brighttech.com',
        action,
        details,
        batchMonth,
        timestamp: Timestamp.now()
      });
    } catch (err) {
      console.error('Error recording audit log:', err);
    }
  };

  const exportPayrollCSV = (payrollList: Payroll[], periodName?: string) => {
    if (!payrollList || payrollList.length === 0) {
      toast.error('No payroll records available to export.');
      return;
    }

    const headers: string[] = ['Employee Name', 'Employee ID', 'Month/Period'];
    if (exportOpts.includeBankDetails) {
      headers.push('Bank Name', 'Account Number');
    }
    if (exportOpts.includeBasicSalary) headers.push('Basic Salary (TZS)');
    if (exportOpts.includeAllowances) headers.push('Allowances (TZS)');
    if (exportOpts.includeGrossPay) headers.push('Gross Pay (TZS)');
    if (exportOpts.includePAYE) headers.push('PAYE Income Tax (TZS)');
    if (exportOpts.includeNSSFEmployee) headers.push('NSSF Employee (10%)');
    if (exportOpts.includeNHIFEmployee) headers.push('NHIF Employee (3%)');
    if (exportOpts.includeHESLB) headers.push('HESLB Loan (15%)');
    if (exportOpts.includeLoansAndOtherDeductions) headers.push('Loans & Other Deductions (TZS)');
    if (exportOpts.includeTotalDeductions) headers.push('Total Deductions (TZS)');
    if (exportOpts.includeNetSalary) headers.push('Net Payable Salary (TZS)');
    if (exportOpts.includeNSSFEmployer) headers.push('NSSF Employer (10%)');
    if (exportOpts.includeSDL) headers.push('SDL (3.5%)');
    if (exportOpts.includeWCF) headers.push('WCF (0.5%)');
    if (exportOpts.includeNHIFEmployer) headers.push('NHIF Employer (3%)');
    if (exportOpts.includeTotalEmployer) headers.push('Total Employer Contribution (TZS)');
    if (exportOpts.includeCompanyCost) headers.push('Total Company Cost (TZS)');
    headers.push('Payment Status');

    // Totals accumulators
    let totBasic = 0, totAllow = 0, totGross = 0, totPaye = 0, totNssfEmpe = 0, totNhifEmpe = 0, totHeslb = 0, totOther = 0, totDeduct = 0, totNet = 0, totNssfEmpr = 0, totSdl = 0, totWcf = 0, totNhifEmpr = 0, totTotalEmpr = 0, totCompanyCost = 0;

    const rows = payrollList.map(p => {
      const emp = employees.find(e => e.id === p.employeeId);
      const allowVal = p.grossPay - p.basicSalary;
      const otherVal = p.otherDeductionsAmount || 0;
      const totalEmplr = p.nssfEmployer + p.sdl + p.wcf + p.nhifEmployer;
      const companyCost = p.grossPay + totalEmplr;

      totBasic += p.basicSalary;
      totAllow += allowVal;
      totGross += p.grossPay;
      totPaye += p.paye;
      totNssfEmpe += p.nssfEmployee;
      totNhifEmpe += p.nhifEmployee;
      totHeslb += (p.heslb || 0);
      totOther += otherVal;
      totDeduct += p.totalDeductions;
      totNet += p.netSalary;
      totNssfEmpr += p.nssfEmployer;
      totSdl += p.sdl;
      totWcf += p.wcf;
      totNhifEmpr += p.nhifEmployer;
      totTotalEmpr += totalEmplr;
      totCompanyCost += companyCost;

      const r: (string | number)[] = [
        `"${(p.employeeName || '').replace(/"/g, '""')}"`,
        `"${(p.employeeId || '').replace(/"/g, '""')}"`,
        `"${p.month}"`
      ];

      if (exportOpts.includeBankDetails) {
        r.push(`"${(emp?.bankName || 'N/A').replace(/"/g, '""')}"`, `"${(emp?.accountNumber || 'N/A').replace(/"/g, '""')}"`);
      }
      if (exportOpts.includeBasicSalary) r.push(p.basicSalary);
      if (exportOpts.includeAllowances) r.push(allowVal);
      if (exportOpts.includeGrossPay) r.push(p.grossPay);
      if (exportOpts.includePAYE) r.push(p.paye);
      if (exportOpts.includeNSSFEmployee) r.push(p.nssfEmployee);
      if (exportOpts.includeNHIFEmployee) r.push(p.nhifEmployee);
      if (exportOpts.includeHESLB) r.push(p.heslb || 0);
      if (exportOpts.includeLoansAndOtherDeductions) r.push(otherVal);
      if (exportOpts.includeTotalDeductions) r.push(p.totalDeductions);
      if (exportOpts.includeNetSalary) r.push(p.netSalary);
      if (exportOpts.includeNSSFEmployer) r.push(p.nssfEmployer);
      if (exportOpts.includeSDL) r.push(p.sdl);
      if (exportOpts.includeWCF) r.push(p.wcf);
      if (exportOpts.includeNHIFEmployer) r.push(p.nhifEmployer);
      if (exportOpts.includeTotalEmployer) r.push(totalEmplr);
      if (exportOpts.includeCompanyCost) r.push(companyCost);
      r.push(`"${p.status.toUpperCase()}"`);

      return r;
    });

    // Totals summary row
    const totalsRow: (string | number)[] = ['"TOTALS"', '""', '""'];
    if (exportOpts.includeBankDetails) {
      totalsRow.push('""', '""');
    }
    if (exportOpts.includeBasicSalary) totalsRow.push(totBasic);
    if (exportOpts.includeAllowances) totalsRow.push(totAllow);
    if (exportOpts.includeGrossPay) totalsRow.push(totGross);
    if (exportOpts.includePAYE) totalsRow.push(totPaye);
    if (exportOpts.includeNSSFEmployee) totalsRow.push(totNssfEmpe);
    if (exportOpts.includeNHIFEmployee) totalsRow.push(totNhifEmpe);
    if (exportOpts.includeHESLB) totalsRow.push(totHeslb);
    if (exportOpts.includeLoansAndOtherDeductions) totalsRow.push(totOther);
    if (exportOpts.includeTotalDeductions) totalsRow.push(totDeduct);
    if (exportOpts.includeNetSalary) totalsRow.push(totNet);
    if (exportOpts.includeNSSFEmployer) totalsRow.push(totNssfEmpr);
    if (exportOpts.includeSDL) totalsRow.push(totSdl);
    if (exportOpts.includeWCF) totalsRow.push(totWcf);
    if (exportOpts.includeNHIFEmployer) totalsRow.push(totNhifEmpr);
    if (exportOpts.includeTotalEmployer) totalsRow.push(totTotalEmpr);
    if (exportOpts.includeCompanyCost) totalsRow.push(totCompanyCost);
    totalsRow.push('""');

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(e => e.join(',')), totalsRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const fileName = periodName ? `Payroll_Excel_${periodName}.csv` : `Payroll_Excel_Report.csv`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${payrollList.length} payroll record(s) to ${fileName}`);
  };

  useEffect(() => {
    const unsubscribeEmployees = onSnapshot(collection(db, 'employees'), (snapshot) => {
      setEmployees(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Employee)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'employees');
    });

    const unsubscribeDeductions = onSnapshot(collection(db, 'deduction_categories'), (snapshot) => {
      setDeductionCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DeductionCategory)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'deduction_categories');
    });

    const unsubscribePayroll = onSnapshot(collection(db, 'payrolls'), (snapshot) => {
      setPayrolls(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Payroll)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'payrolls');
    });

    const unsubscribeCompliance = onSnapshot(collection(db, 'compliance_records'), (snapshot) => {
      setComplianceRecords(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ComplianceRecord)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'compliance_records');
    });

    const unsubscribeAttendance = onSnapshot(collection(db, 'attendance'), (snapshot) => {
      setAttendanceLogs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'attendance');
      setLoading(false);
    });

    const qLogs = query(collection(db, 'payrollAuditLogs'), orderBy('timestamp', 'desc'));
    const unsubscribeAuditLogs = onSnapshot(qLogs, (snapshot) => {
      setAuditLogs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'payrollAuditLogs');
    });

    // Clock timer ticking every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      unsubscribeEmployees();
      unsubscribeDeductions();
      unsubscribePayroll();
      unsubscribeCompliance();
      unsubscribeAttendance();
      unsubscribeAuditLogs();
      clearInterval(timer);
    };
  }, []);

  const profileEmail = profile?.email;
  useEffect(() => {
    if (employees.length === 0) return;
    const matched = profileEmail 
      ? employees.find(emp => emp.email?.toLowerCase() === profileEmail.toLowerCase())
      : null;
    if (matched) {
      setAttendanceEmployeeId(matched.id);
    } else {
      setAttendanceEmployeeId(employees[0].id);
    }
  }, [profileEmail, employees.length]);

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = (emp.firstName + ' ' + emp.lastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.department.toLowerCase().includes(searchTerm.toLowerCase());
    
    const empCat = getEmployeeCategory(emp);
    const matchesCategory = categoryFilter === 'ALL' || empCat.toLowerCase() === categoryFilter.toLowerCase();

    if (!matchesCategory) return false;

    if (isAdmin || isHR) return matchesSearch;
    if (isHOD && profile?.hodDepartments) {
      return matchesSearch && profile.hodDepartments.includes(emp.department);
    }
    return matchesSearch;
  });

  const filteredPayrolls = payrolls.filter(p => {
    const matchesSearch = p.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.month.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesSalaryRange = true;
    if (salaryRangeFilter === 'under1m') {
      matchesSalaryRange = p.basicSalary < 1000000;
    } else if (salaryRangeFilter === '1m_to_3m') {
      matchesSalaryRange = p.basicSalary >= 1000000 && p.basicSalary <= 3000000;
    } else if (salaryRangeFilter === 'above3m') {
      matchesSalaryRange = p.basicSalary > 3000000;
    }

    if (!matchesSalaryRange) return false;

    if (isAdmin || isHR) return matchesSearch;
    if (isHOD && profile?.hodDepartments) {
      const employee = employees.find(e => e.id === p.employeeId);
      return matchesSearch && employee && profile.hodDepartments.includes(employee.department);
    }
    return false;
  });

  const handleDownloadEmployeeTemplate = () => {
    const templateData = [
      {
        'Employee Code': 'EMP-001',
        'First Name': 'John',
        'Last Name': 'Doe',
        'Email': 'john.doe@company.co.tz',
        'Phone': '+255 712 345 678',
        'Department': 'Technical',
        'Position': 'Senior HVAC Technician',
        'Category': 'Technician',
        'Basic Salary (TZS)': 1200000,
        'TIN': '123-456-789',
        'NSSF Number': '1000200030',
        'NHIF Number': '987654321',
        'WCF Number': 'WCF-00123',
        'Bank Name': 'CRDB Bank',
        'Account Number': '0150123456700',
        'Joining Date (YYYY-MM-DD)': '2024-01-15',
        'Status': 'active',
        'HESLB Loan': 'No',
        'HESLB Control No': '',
        'HESLB Balance': 0,
        'OSHA Reg No': 'OSHA-2024-01',
        'OSHA Medical Exam Date': '2024-02-01',
        'OSHA Status': 'compliant'
      },
      {
        'Employee Code': 'EMP-002',
        'First Name': 'Jane',
        'Last Name': 'Smith',
        'Email': 'jane.smith@company.co.tz',
        'Phone': '+255 754 987 654',
        'Department': 'Customer Care',
        'Position': 'Customer Care Representative',
        'Category': 'Customer Care',
        'Basic Salary (TZS)': 950000,
        'TIN': '987-654-321',
        'NSSF Number': '1000200031',
        'NHIF Number': '987654322',
        'WCF Number': 'WCF-00124',
        'Bank Name': 'NMB Bank',
        'Account Number': '2001234567890',
        'Joining Date (YYYY-MM-DD)': '2024-03-01',
        'Status': 'active',
        'HESLB Loan': 'Yes',
        'HESLB Control No': '99123456789',
        'HESLB Balance': 1500000,
        'OSHA Reg No': '',
        'OSHA Medical Exam Date': '',
        'OSHA Status': 'exempt'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees Template');
    XLSX.writeFile(workbook, 'Employee_Import_Template.xlsx');
    toast.success('Employee import Excel template downloaded successfully');
  };

  const handleEmployeeExcelFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setEmployeeExcelFileName(file.name);
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawRows: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (!rawRows || rawRows.length === 0) {
          toast.error('The selected Excel file contains no data rows');
          return;
        }

        const parsed: ParsedEmployeeRow[] = rawRows.map((row, idx) => {
          const employeeCode = String(row['Employee Code'] || row['EmployeeCode'] || row['Code'] || row['EMP CODE'] || row['EMPLOYEE CODE'] || '').trim();
          const firstName = String(row['First Name'] || row['FirstName'] || row['FIRST NAME'] || row['First_Name'] || row['Name'] || '').trim();
          const lastName = String(row['Last Name'] || row['LastName'] || row['LAST NAME'] || row['Last_Name'] || '').trim();
          const email = String(row['Email'] || row['EMAIL'] || row['Email Address'] || row['EmailAddress'] || '').trim().toLowerCase();
          const phone = String(row['Phone'] || row['PHONE'] || row['Phone Number'] || row['Mobile'] || '').trim();
          const department = String(row['Department'] || row['DEPARTMENT'] || row['Dept'] || 'General').trim() || 'General';
          const position = String(row['Position'] || row['POSITION'] || row['Job Title'] || row['Title'] || 'Staff').trim() || 'Staff';
          const rawCategory = String(row['Category'] || row['CATEGORY'] || row['Employee Category'] || 'General').trim();
          const category = EMPLOYEE_CATEGORIES.find(c => c.toLowerCase() === rawCategory.toLowerCase()) || 'General';
          
          const rawSalary = row['Basic Salary (TZS)'] ?? row['Basic Salary'] ?? row['Salary'] ?? row['BASIC SALARY'] ?? row['SALARY'] ?? 0;
          const salary = Math.max(0, parseFloat(String(rawSalary)) || 0);

          const tin = String(row['TIN'] || row['Tin'] || row['TIN Number'] || '').trim();
          const nssfNumber = String(row['NSSF Number'] || row['NSSF'] || row['NSSF No'] || '').trim();
          const nhifNumber = String(row['NHIF Number'] || row['NHIF'] || row['NHIF No'] || '').trim();
          const wcfNumber = String(row['WCF Number'] || row['WCF'] || row['WCF No'] || '').trim();
          const bankName = String(row['Bank Name'] || row['Bank'] || row['BANK NAME'] || '').trim();
          const accountNumber = String(row['Account Number'] || row['Account No'] || row['Account'] || row['ACCOUNT NUMBER'] || '').trim();
          
          const rawJoiningDate = String(row['Joining Date (YYYY-MM-DD)'] || row['Joining Date'] || row['JoiningDate'] || row['JOINING DATE'] || row['Date Joined'] || '').trim();
          const joiningDate = rawJoiningDate && !isNaN(Date.parse(rawJoiningDate)) 
            ? new Date(rawJoiningDate).toISOString().split('T')[0] 
            : new Date().toISOString().split('T')[0];

          const rawStatus = String(row['Status'] || row['STATUS'] || 'active').trim().toLowerCase();
          const status: 'active' | 'inactive' = rawStatus === 'inactive' ? 'inactive' : 'active';

          const rawHeslbLoan = String(row['HESLB Loan'] || row['HESLB'] || row['Has HESLB Loan'] || '').trim().toLowerCase();
          const hasHeslbLoan = rawHeslbLoan === 'yes' || rawHeslbLoan === 'true' || rawHeslbLoan === '1';
          const heslbControlNo = String(row['HESLB Control No'] || row['HESLB Control Number'] || '').trim();
          const rawHeslbBalance = row['HESLB Balance'] ?? row['Heslb Balance'] ?? 0;
          const heslbBalance = Math.max(0, parseFloat(String(rawHeslbBalance)) || 0);

          const oshaRegistrationNo = String(row['OSHA Reg No'] || row['OSHA Registration No'] || '').trim();
          const rawOshaDate = String(row['OSHA Medical Exam Date'] || row['OSHA Date'] || '').trim();
          const oshaMedicalExamDate = rawOshaDate && !isNaN(Date.parse(rawOshaDate)) ? new Date(rawOshaDate).toISOString().split('T')[0] : '';
          const rawOshaStatus = String(row['OSHA Status'] || row['Osha Status'] || 'compliant').trim().toLowerCase();
          const oshaStatus: 'compliant' | 'pending_exam' | 'expired' | 'exempt' = 
            ['compliant', 'pending_exam', 'expired', 'exempt'].includes(rawOshaStatus) ? (rawOshaStatus as any) : 'compliant';

          const validationErrors: string[] = [];
          if (!firstName) validationErrors.push('First name is required');
          if (!lastName) validationErrors.push('Last name is required');
          if (!email) validationErrors.push('Email is required');

          return {
            rowIndex: idx + 1,
            employeeCode,
            firstName,
            lastName,
            email,
            phone,
            department,
            position,
            category,
            salary,
            tin,
            nssfNumber,
            nhifNumber,
            wcfNumber,
            bankName,
            accountNumber,
            joiningDate,
            status,
            hasHeslbLoan,
            heslbControlNo,
            heslbBalance,
            oshaRegistrationNo,
            oshaMedicalExamDate,
            oshaStatus,
            selected: validationErrors.length === 0,
            validationErrors
          };
        });

        setImportedEmployeeRows(parsed);
        setIsImportEmployeeModalOpen(true);
        toast.success(`Parsed ${parsed.length} employee row(s) from Excel file.`);
      } catch (err: any) {
        toast.error(`Error reading Excel file: ${err.message || 'Invalid format'}`);
      } finally {
        if (e.target) e.target.value = '';
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleProcessEmployeeImport = async () => {
    const selectedRows = importedEmployeeRows.filter(r => r.selected && r.validationErrors.length === 0);
    if (selectedRows.length === 0) {
      toast.error('No valid employee rows selected for import.');
      return;
    }

    setIsImportingEmployees(true);
    let importedCount = 0;
    let updatedCount = 0;

    try {
      for (const row of selectedRows) {
        const existingEmp = employees.find(e => e.email.toLowerCase() === row.email.toLowerCase());
        const empData = {
          employeeCode: row.employeeCode,
          firstName: row.firstName,
          lastName: row.lastName,
          email: row.email.toLowerCase(),
          phone: row.phone,
          department: row.department,
          position: row.position,
          category: row.category,
          salary: Number(row.salary),
          tin: row.tin,
          nssfNumber: row.nssfNumber,
          nhifNumber: row.nhifNumber,
          wcfNumber: row.wcfNumber,
          bankName: row.bankName,
          accountNumber: row.accountNumber,
          joiningDate: Timestamp.fromDate(new Date(row.joiningDate)),
          status: row.status,
          hasHeslbLoan: row.hasHeslbLoan,
          heslbControlNo: row.heslbControlNo,
          heslbBalance: Number(row.heslbBalance),
          oshaRegistrationNo: row.oshaRegistrationNo,
          oshaMedicalExamDate: row.oshaMedicalExamDate,
          oshaStatus: row.oshaStatus,
          allowances: existingEmp?.allowances || [],
          createdAt: existingEmp?.createdAt || Timestamp.now()
        };

        if (existingEmp) {
          await updateDoc(doc(db, 'employees', existingEmp.id), empData);
          updatedCount++;
        } else {
          await addDoc(collection(db, 'employees'), empData);
          importedCount++;
        }
      }

      toast.success(`Import completed! Created ${importedCount} new employee(s) and updated ${updatedCount} existing profile(s).`);
      setIsImportEmployeeModalOpen(false);
      setImportedEmployeeRows([]);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'employees');
      toast.error('Failed to process employee import.');
    } finally {
      setIsImportingEmployees(false);
    }
  };

  const handleEmployeeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const data = {
        ...employeeForm,
        email: employeeForm.email.toLowerCase(),
        salary: Number(employeeForm.salary),
        hasHeslbLoan: employeeForm.hasHeslbLoan,
        taxResidency: employeeForm.taxResidency || 'resident',
        isTaxExempt: Boolean(employeeForm.isTaxExempt),
        heslbBalance: Number(employeeForm.heslbBalance) || 0,
        joiningDate: Timestamp.fromDate(new Date(employeeForm.joiningDate)),
        createdAt: Timestamp.now()
      };
      
      if (editingEmployee) {
        await withRetry(async () => {
          await updateDoc(doc(db, 'employees', editingEmployee.id), data);
        });
        toast.success('Employee updated successfully');
      } else {
        await withRetry(async () => {
          await addDoc(collection(db, 'employees'), data);
        });
        toast.success('Employee added successfully');
      }
      
      setIsEmployeeModalOpen(false);
      setEditingEmployee(null);
      setEmployeeForm({
        employeeCode: '', firstName: '', lastName: '', email: '', phone: '', department: '', position: '', category: 'general',
        salary: 0, tin: '', nssfNumber: '', nhifNumber: '', wcfNumber: '', oshaRegistrationNo: '',
        oshaMedicalExamDate: '', oshaStatus: 'compliant', heslbControlNo: '', heslbBalance: 0,
        bankName: '', accountNumber: '', joiningDate: new Date().toISOString().split('T')[0], status: 'active',
        hasHeslbLoan: false, taxResidency: 'resident', isTaxExempt: false, allowances: []
      });
    } catch (error) {
      handleFirestoreError(error, editingEmployee ? OperationType.UPDATE : OperationType.CREATE, 'employees');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeductionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const principal = Number(deductionForm.totalPrincipal) || 0;
      const rate = Number(deductionForm.interestRate) || 0;
      const duration = Number(deductionForm.durationMonths) || 12;
      const isLoan = deductionForm.categoryKind === 'loan' || deductionForm.categoryKind === 'advance';
      
      const totalRet = isLoan 
        ? Math.round(principal + (principal * (rate / 100) * (duration / 12)))
        : principal;
      const installment = duration > 0 ? Math.round(totalRet / duration) : Number(deductionForm.value) || 0;
      const remBal = editingDeduction ? Number(deductionForm.remainingBalance) || totalRet : totalRet;

      const data = {
        ...deductionForm,
        value: installment || Number(deductionForm.value) || 0,
        totalPrincipal: principal,
        totalReturn: totalRet,
        remainingBalance: remBal,
        monthlyInstallment: installment,
        loanDate: deductionForm.loanDate || new Date().toISOString().split('T')[0],
        durationMonths: duration,
        interestRate: rate,
        monthsPaid: Number(deductionForm.monthsPaid) || 0,
        updatedAt: Timestamp.now()
      };

      if (editingDeduction) {
        await updateDoc(doc(db, 'deduction_categories', editingDeduction.id), data);
        toast.success('Deduction category updated successfully');
      } else {
        await addDoc(collection(db, 'deduction_categories'), {
          ...data,
          createdAt: Timestamp.now()
        });
        toast.success('Deduction category added successfully');
      }
      setIsDeductionModalOpen(false);
      setEditingDeduction(null);
      setDeductionForm({ 
        name: '', description: '', type: 'fixed', value: 0, categoryKind: 'loan', 
        totalPrincipal: 0, totalReturn: 0, remainingBalance: 0, monthlyInstallment: 0, 
        loanDate: new Date().toISOString().split('T')[0], durationMonths: 12, interestRate: 0, monthsPaid: 0, assignedEmployeeIds: [] 
      });
    } catch (error) {
      handleFirestoreError(error, editingDeduction ? OperationType.UPDATE : OperationType.CREATE, 'deduction_categories');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePayroll = async (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Payroll Record',
      message: 'Are you sure you want to delete this payroll record? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'payrolls', id));
          toast.success('Payroll record deleted');
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, 'payrolls');
        }
      }
    });
  };

  const generateSalarySlip = (payroll: Payroll, mode: 'download' | 'print' = 'download') => {
    const employee = employees.find(e => e.id === payroll.employeeId);
    if (!employee) return;

    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(30, 64, 175);
    doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('BRIGHT TECHNICAL SYSTEMS', 105, 18, { align: 'center' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Specialists in Air Conditioning, Plumbing & Electrical Works', 105, 24, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('PAYSLIP - ' + payroll.month, 105, 32, { align: 'center' });
    
    const itemsStatutory = [
      { name: 'NSSF (Employee 10%)', amount: payroll.nssfEmployee },
      { name: 'PAYE (Income Tax)', amount: payroll.paye },
      { name: 'NHIF (Employee 3%)', amount: payroll.nhifEmployee },
      { name: 'HESLB (Loan)', amount: payroll.heslb }
    ].filter(i => i.amount > 0);

    let currentY = 55;
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('EMPLOYEE INFORMATION', 20, currentY);
    doc.setDrawColor(226, 232, 240);
    doc.line(20, currentY + 2, 190, currentY + 2);
    
    currentY += 10;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setDrawColor(241, 245, 249);
    doc.setFillColor(248, 250, 252);
    doc.rect(20, currentY - 5, 170, 32, 'F');
    
    doc.text(`Employee Name:`, 25, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(`${employee.firstName} ${employee.lastName}`, 60, currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Employee ID:`, 120, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(employee.employeeCode || employee.id.substring(0, 8).toUpperCase(), 150, currentY);
    
    currentY += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`Department:`, 25, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(employee.department || 'N/A', 60, currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Position:`, 120, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(employee.position || 'N/A', 150, currentY);
    
    currentY += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`TIN Number:`, 25, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(employee.tin || 'N/A', 60, currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Month / Year:`, 120, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(payroll.month, 150, currentY);

    currentY += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`Bank Info:`, 25, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(`${employee.bankName || 'N/A'} - ${employee.accountNumber || 'N/A'}`, 60, currentY);

    currentY += 20;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('EARNINGS', 20, currentY);
    doc.text('DEDUCTIONS', 110, currentY);
    doc.line(20, currentY + 2, 95, currentY + 2);
    doc.line(110, currentY + 2, 190, currentY + 2);
    
    currentY += 10;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    let earnY = currentY;
    doc.text('Basic Salary', 25, earnY);
    doc.text(`TZS ${payroll.basicSalary.toLocaleString()}`, 90, earnY, { align: 'right' });
    earnY += 6;

    employee.allowances?.forEach(al => {
      doc.text(al.name, 25, earnY);
      doc.text(`TZS ${al.amount.toLocaleString()}`, 90, earnY, { align: 'right' });
      earnY += 6;
    });
    
    let dedY = currentY;
    itemsStatutory.forEach(s => {
      doc.text(s.name, 115, dedY);
      doc.text(`TZS ${s.amount.toLocaleString()}`, 185, dedY, { align: 'right' });
      dedY += 6;
    });
    
    payroll.deductions.forEach(d => {
      doc.text(d.categoryName, 115, dedY);
      doc.text(`TZS ${d.amount.toLocaleString()}`, 185, dedY, { align: 'right' });
      dedY += 6;
    });
    
    currentY = Math.max(earnY + 10, dedY + 10);
    doc.setDrawColor(226, 232, 240);
    doc.line(20, currentY, 190, currentY);
    currentY += 10;
    
    doc.setFont('helvetica', 'bold');
    doc.text('GROSS EARNINGS:', 25, currentY);
    doc.text(`TZS ${payroll.grossPay.toLocaleString()}`, 90, currentY, { align: 'right' });
    
    doc.text('TOTAL DEDUCTIONS:', 115, currentY);
    doc.text(`TZS ${payroll.totalDeductions.toLocaleString()}`, 185, currentY, { align: 'right' });
    
    currentY += 15;
    doc.setFillColor(30, 64, 185); // Professional Navy Blue
    doc.rect(20, currentY - 8, 170, 15, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text('NET PAY (PAYABLE):', 30, currentY + 1);
    doc.text(`TZS ${payroll.netSalary.toLocaleString()}`, 180, currentY + 1, { align: 'right' });
    
    currentY += 25;
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(10);
    doc.text('EMPLOYER CONTRIBUTIONS (STATUTORY)', 20, currentY);
    doc.line(20, currentY + 2, 100, currentY + 2);
    currentY += 10;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('NSSF Employer (10%)', 25, currentY);
    doc.text(`TZS ${payroll.nssfEmployer.toLocaleString()}`, 90, currentY, { align: 'right' });
    currentY += 6;
    doc.text('SDL (Skills Development Levy 4%)', 25, currentY);
    doc.text(`TZS ${payroll.sdl.toLocaleString()}`, 90, currentY, { align: 'right' });
    currentY += 6;
    doc.text('WCF (Workers Comp. Fund 0.5%)', 25, currentY);
    doc.text(`TZS ${payroll.wcf.toLocaleString()}`, 90, currentY, { align: 'right' });

    currentY += 25;
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('------------------------------------------', 105, currentY, { align: 'center' });
    doc.text('Employer Signature & Stamp', 105, currentY + 5, { align: 'center' });

    currentY += 20;
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.text('This is a computer generated salary slip and does not require a signature.', 105, currentY, { align: 'center' });
    doc.text('Professional Tanzania Payroll - Compliant with TRA and NSSF Regulations', 105, currentY + 4, { align: 'center' });
    
    downloadFile(doc.output('blob'), `Salary_Slip_${employee.firstName}_${payroll.month}.pdf`);
  };

  const calculateTanzaniaPAYE = (taxableIncome: number, residency: 'resident' | 'non-resident' = 'resident', isExempt: boolean = false) => {
    if (isExempt) return 0;
    if (residency === 'non-resident') {
      return taxableIncome * 0.15;
    }
    if (taxableIncome <= 270000) return 0;
    if (taxableIncome <= 520000) return (taxableIncome - 270000) * 0.08;
    if (taxableIncome <= 760000) return 20000 + (taxableIncome - 520000) * 0.20;
    if (taxableIncome <= 1000000) return 68000 + (taxableIncome - 760000) * 0.25;
    return 128000 + (taxableIncome - 1000000) * 0.30;
  };

  const getEmployeeLastStatus = (employeeId: string) => {
    const employeeLogs = attendanceLogs.filter(log => log.employeeId === employeeId);
    if (employeeLogs.length === 0) return null;
    return employeeLogs[0];
  };

  const performCheckInOut = async (type: 'check-in' | 'check-out', employeeToLog: Employee, note: string) => {
    setGeoLoading(true);
    setGeoError(null);
    
    const handleSave = async (lat: number | null, lng: number | null, errMessage: string | null = null) => {
      try {
        await addDoc(collection(db, 'attendance'), {
          employeeId: employeeToLog.id,
          employeeCode: employeeToLog.employeeCode || '',
          employeeName: `${employeeToLog.firstName} ${employeeToLog.lastName}`,
          employeeEmail: employeeToLog.email,
          department: employeeToLog.department,
          position: employeeToLog.position || '',
          type,
          timestamp: Timestamp.now(),
          latitude: lat,
          longitude: lng,
          locationError: errMessage || null,
          note: note.trim(),
          deviceInfo: navigator.userAgent
        });
        toast.success(`Successfully ${type === 'check-in' ? 'checked in' : 'checked out'}!`);
        setAttendanceNote('');
      } catch (err) {
        handleFirestoreError(err, OperationType.CREATE, 'attendance');
      } finally {
        setGeoLoading(false);
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setGeoCoords({ latitude, longitude });
          handleSave(latitude, longitude);
        },
        (error) => {
          console.error("Geolocation error:", error);
          let errMsg = 'Unknown error';
          if (error.code === error.PERMISSION_DENIED) {
            errMsg = 'Permission denied';
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            errMsg = 'Position unavailable';
          } else if (error.code === error.TIMEOUT) {
            errMsg = 'Timeout';
          }
          setGeoError(errMsg);
          toast.warning(`Saved timestamp, but location could not be captured: ${errMsg}`);
          handleSave(null, null, errMsg);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      const errMsg = 'Geolocation not supported by browser';
      setGeoError(errMsg);
      toast.warning(errMsg);
      handleSave(null, null, errMsg);
    }
  };

  const sendSalarySlipEmail = async (payroll: Payroll) => {
    const employee = employees.find(e => e.id === payroll.employeeId);
    if (!employee || !employee.email) {
      toast.error('Employee email not found');
      return;
    }

    try {
      const doc = new jsPDF();
      
      // Header
      doc.setFillColor(30, 64, 175);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('BRIGHT TECHNICAL SYSTEMS', 105, 18, { align: 'center' });
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Specialists in Air Conditioning, Plumbing & Electrical Works', 105, 24, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('PAYSLIP - ' + payroll.month, 105, 32, { align: 'center' });
      
      const itemsStatutory = [
        { name: 'NSSF (Employee 10%)', amount: payroll.nssfEmployee },
        { name: 'PAYE (Income Tax)', amount: payroll.paye },
        { name: 'NHIF (Employee 3%)', amount: payroll.nhifEmployee },
        { name: 'HESLB (Loan)', amount: payroll.heslb }
      ].filter(i => i.amount > 0);

      let currentY = 55;
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('EMPLOYEE INFORMATION', 20, currentY);
      doc.setDrawColor(226, 232, 240);
      doc.line(20, currentY + 2, 190, currentY + 2);
      
      currentY += 10;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setDrawColor(241, 245, 249);
      doc.setFillColor(248, 250, 252);
      doc.rect(20, currentY - 5, 170, 32, 'F');
      
      doc.text(`Employee Name:`, 25, currentY);
      doc.setFont('helvetica', 'bold');
      doc.text(`${employee.firstName} ${employee.lastName}`, 60, currentY);
      
      doc.setFont('helvetica', 'normal');
      doc.text(`Employee ID:`, 120, currentY);
      doc.setFont('helvetica', 'bold');
      doc.text(employee.employeeCode || employee.id.substring(0, 8).toUpperCase(), 150, currentY);
      
      currentY += 7;
      doc.setFont('helvetica', 'normal');
      doc.text(`Department:`, 25, currentY);
      doc.setFont('helvetica', 'bold');
      doc.text(employee.department || 'N/A', 60, currentY);
      
      doc.setFont('helvetica', 'normal');
      doc.text(`Position:`, 120, currentY);
      doc.setFont('helvetica', 'bold');
      doc.text(employee.position || 'N/A', 150, currentY);
      
      currentY += 7;
      doc.setFont('helvetica', 'normal');
      doc.text(`TIN Number:`, 25, currentY);
      doc.setFont('helvetica', 'bold');
      doc.text(employee.tin || 'N/A', 60, currentY);
      
      doc.setFont('helvetica', 'normal');
      doc.text(`Month / Year:`, 120, currentY);
      doc.setFont('helvetica', 'bold');
      doc.text(payroll.month, 150, currentY);

      currentY += 7;
      doc.setFont('helvetica', 'normal');
      doc.text(`Bank Info:`, 25, currentY);
      doc.setFont('helvetica', 'bold');
      doc.text(`${employee.bankName || 'N/A'} - ${employee.accountNumber || 'N/A'}`, 60, currentY);

      currentY += 20;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('EARNINGS', 20, currentY);
      doc.text('DEDUCTIONS', 110, currentY);
      doc.line(20, currentY + 2, 95, currentY + 2);
      doc.line(110, currentY + 2, 190, currentY + 2);
      
      currentY += 10;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      let earnY = currentY;
      doc.text('Basic Salary', 25, earnY);
      doc.text(`TZS ${payroll.basicSalary.toLocaleString()}`, 90, earnY, { align: 'right' });
      earnY += 6;

      employee.allowances?.forEach(al => {
        doc.text(al.name, 25, earnY);
        doc.text(`TZS ${al.amount.toLocaleString()}`, 90, earnY, { align: 'right' });
        earnY += 6;
      });
      
      let dedY = currentY;
      itemsStatutory.forEach(s => {
        doc.text(s.name, 115, dedY);
        doc.text(`TZS ${s.amount.toLocaleString()}`, 185, dedY, { align: 'right' });
        dedY += 6;
      });
      
      payroll.deductions.forEach(d => {
        doc.text(d.categoryName, 115, dedY);
        doc.text(`TZS ${d.amount.toLocaleString()}`, 185, dedY, { align: 'right' });
        dedY += 6;
      });
      
      currentY = Math.max(earnY + 10, dedY + 10);
      doc.setDrawColor(226, 232, 240);
      doc.line(20, currentY, 190, currentY);
      currentY += 10;
      
      doc.setFont('helvetica', 'bold');
      doc.text('GROSS EARNINGS:', 25, currentY);
      doc.text(`TZS ${payroll.grossPay.toLocaleString()}`, 90, currentY, { align: 'right' });
      
      doc.text('TOTAL DEDUCTIONS:', 115, currentY);
      doc.text(`TZS ${payroll.totalDeductions.toLocaleString()}`, 185, currentY, { align: 'right' });
      
      currentY += 15;
      doc.setFillColor(30, 64, 185);
      doc.rect(20, currentY - 8, 170, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.text('NET PAY (PAYABLE):', 30, currentY + 1);
      doc.text(`TZS ${payroll.netSalary.toLocaleString()}`, 180, currentY + 1, { align: 'right' });
      
      currentY += 25;
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(10);
      doc.text('EMPLOYER CONTRIBUTIONS (STATUTORY)', 20, currentY);
      doc.line(20, currentY + 2, 100, currentY + 2);
      currentY += 10;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('NSSF Employer (10%)', 25, currentY);
      doc.text(`TZS ${payroll.nssfEmployer.toLocaleString()}`, 90, currentY, { align: 'right' });
      currentY += 6;
      doc.text('SDL (Skills Development Levy 4%)', 25, currentY);
      doc.text(`TZS ${payroll.sdl.toLocaleString()}`, 90, currentY, { align: 'right' });
      currentY += 6;
      doc.text('WCF (Workers Comp. Fund 0.5%)', 25, currentY);
      doc.text(`TZS ${payroll.wcf.toLocaleString()}`, 90, currentY, { align: 'right' });

      currentY += 25;
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('------------------------------------------', 105, currentY, { align: 'center' });
      doc.text('Employer Signature & Stamp', 105, currentY + 5, { align: 'center' });
      
      currentY += 20;
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text('This is a computer generated salary slip and does not require a signature.', 105, currentY, { align: 'center' });
      doc.text('Professional Tanzania Payroll - Compliant with TRA and NSSF Regulations', 105, currentY + 4, { align: 'center' });

      const pdfBase64 = doc.output('datauristring').split(',')[1];

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: employee.email,
          subject: `Salary Slip - ${payroll.month}`,
          body: `Dear ${employee.firstName},\n\nPlease find attached your salary slip for ${payroll.month}.\n\nBest regards,\nHR Department`,
          attachments: [{
            filename: `Salary_Slip_${employee.firstName}_${payroll.month}.pdf`,
            content: pdfBase64,
            encoding: 'base64'
          }]
        })
      });

      if (response.ok) {
        toast.success(`Salary slip sent to ${employee.email}`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending salary slip:', error);
      toast.error('Failed to send salary slip email');
    }
  };

  const [showPayrollPreview, setShowPayrollPreview] = useState(false);
  const [payrollSuccessPopupMessage, setPayrollSuccessPopupMessage] = useState<string | null>(null);

  const handlePayrollSubmit = async (e: React.FormEvent | React.MouseEvent) => {
    if ('preventDefault' in e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (payrollForm.employeeIds.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }
    
    if (!showPayrollPreview) {
      setShowPayrollPreview(true);
      return;
    }

    await handleAsyncAction(async () => {
      let createdCount = 0;
      const payrollItems: any[] = [];

      for (const employeeId of payrollForm.employeeIds) {
        const employee = employees.find(emp => emp.id === employeeId);
        if (!employee) continue;

        const allowancesAmount = (employee.allowances || []).reduce((sum, al) => sum + al.amount, 0);
        const grossPay = (employee.salary || 0) + allowancesAmount;
        
        const statutoryOpt = payrollForm.employeeStatutoryOpt?.[employeeId] || { nssf: true, nhif: true, paye: true, heslb: true };

        const pensionableEarnings = employee.salary || 0;
        const nssfEmployee = statutoryOpt.nssf ? pensionableEarnings * 0.10 : 0;
        const nssfEmployer = statutoryOpt.nssf ? pensionableEarnings * 0.10 : 0;

        const isExempt = payrollForm.employeeTaxExempt?.[employeeId] ?? (employee.isTaxExempt || false);
        const residency = payrollForm.employeeTaxResidency?.[employeeId] || employee.taxResidency || 'resident';

        const taxablePay = (isExempt || !statutoryOpt.paye) ? 0 : Math.max(0, grossPay - nssfEmployee);
        const paye = (statutoryOpt.paye && !isExempt) ? calculateTanzaniaPAYE(taxablePay, residency, isExempt) : 0;
        const nhifEmployee = statutoryOpt.nhif ? grossPay * 0.03 : 0;
        const heslb = (statutoryOpt.heslb && employee.hasHeslbLoan) ? (employee.salary || 0) * 0.15 : 0;
        
        const assignedCatIds = payrollForm.employeeCustomDeductions?.[employeeId] || [];
        const employeeDeductionsList: PayrollDeduction[] = [];
        let otherDeductionsAmount = 0;

        for (const catId of assignedCatIds) {
          const cat = deductionCategories.find(c => c.id === catId);
          if (cat) {
            const amt = cat.type === 'percentage' 
              ? ((employee.salary || 0) * (cat.value || 0)) / 100 
              : (cat.value || 0);

            const isLoan = cat.categoryKind === 'loan' || cat.categoryKind === 'advance';
            let newBalance = cat.remainingBalance !== undefined ? cat.remainingBalance : (cat.totalReturn || cat.totalPrincipal || 0);
            let monthsPaid = cat.monthsPaid || 0;
            const duration = cat.durationMonths || 12;

            if (isLoan && newBalance > 0) {
              newBalance = Math.max(0, newBalance - amt);
              monthsPaid = Math.min(duration, monthsPaid + 1);
              try {
                await updateDoc(doc(db, 'deduction_categories', cat.id), {
                  remainingBalance: newBalance,
                  monthsPaid,
                  updatedAt: Timestamp.now()
                });
              } catch (e) {
                console.error("Error updating loan balance", e);
              }
            }

            employeeDeductionsList.push({
              categoryId: cat.id,
              categoryName: cat.name,
              amount: amt,
              type: cat.type || 'fixed',
              totalPrincipal: cat.totalPrincipal || 0,
              remainingBalance: isLoan ? newBalance : undefined,
              monthlyInstallment: cat.monthlyInstallment || amt,
              durationMonths: duration,
              monthsPaid,
              monthsRemaining: Math.max(0, duration - monthsPaid)
            });
            otherDeductionsAmount += amt;
          }
        }

        const totalDeductionsEmployee = nssfEmployee + paye + nhifEmployee + heslb + otherDeductionsAmount;
        const netSalary = grossPay - totalDeductionsEmployee;

        const nhifEmployer = 0; 
        const sdl = grossPay * 0.035; 
        const wcf = grossPay * 0.01;   
        const totalEmployerCost = grossPay + nssfEmployer + sdl + wcf;

        // Check if payroll already exists
        const existing = payrolls.find(p => p.employeeId === employeeId && p.month === payrollForm.month);
        if (existing) {
          if (existing.status === 'paid') {
            continue;
          }
        }

        const data: any = {
          id: existing ? existing.id : undefined,
          employeeId: employee.id || '',
          employeeCode: employee.employeeCode || employee.id?.substring(0, 8).toUpperCase() || '',
          employeeName: `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Employee',
          employeeEmail: employee.email && isValidEmail(employee.email) ? employee.email : '',
          department: employee.department || 'General',
          month: payrollForm.month,
          basicSalary: employee.salary || 0,
          allowancesAmount,
          grossPay,
          taxablePay,
          nssfEmployee,
          paye,
          nhifEmployee,
          heslb,
          nssfEmployer,
          nhifEmployer,
          sdl,
          wcf,
          deductions: employeeDeductionsList,
          markedDeductions: assignedCatIds,
          otherDeductionsAmount,
          totalDeductions: totalDeductionsEmployee,
          netSalary,
          totalEmployerCost,
          status: 'pending',
          createdAt: existing ? existing.createdAt : Timestamp.now(),
          updatedAt: Timestamp.now()
        };

        payrollItems.push(data);
        createdCount++;
      }

      if (payrollItems.length > 0) {
        await processPayrollWithTransaction(payrollItems);
      }

      const msg = `Payroll successfully created and regenerated for ${createdCount} employee(s) for month ${payrollForm.month}!`;
      setPayrollSuccessPopupMessage(msg);
      
      setIsPayrollModalOpen(false);
      setShowPayrollPreview(false);
      setPayrollForm({ employeeIds: [], month: format(new Date(), 'yyyy-MM'), deductions: [], employeeCustomDeductions: {}, employeeTaxResidency: {}, employeeTaxExempt: {}, employeeStatutoryOpt: {} });
    }, setIsSubmitting, {
      timeoutMs: 0,
      successMessage: 'Payroll successfully processed and created!',
      errorMessage: 'Failed to generate payroll',
      onError: (error) => {
        console.error('Error generating payroll:', error);
      }
    });
  };

  const handleComplianceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!complianceForm.employeeId) {
      toast.error('Please select an employee');
      return;
    }
    setIsSubmitting(true);
    try {
      const emp = employees.find(e => e.id === complianceForm.employeeId);
      const data: Omit<ComplianceRecord, 'id'> = {
        employeeId: complianceForm.employeeId,
        employeeName: emp ? `${emp.firstName} ${emp.lastName}` : 'Employee',
        authority: complianceForm.authority,
        title: complianceForm.title,
        referenceNumber: complianceForm.referenceNumber,
        claimAmount: complianceForm.claimAmount || 0,
        status: complianceForm.status,
        incidentOrEventDate: complianceForm.incidentOrEventDate,
        expiryOrDueDate: complianceForm.expiryOrDueDate,
        notes: complianceForm.notes,
        createdAt: Timestamp.now()
      };
      await addDoc(collection(db, 'compliance_records'), data);
      toast.success('Statutory compliance record/claim saved');
      setIsComplianceModalOpen(false);
      setComplianceForm({
        employeeId: '',
        authority: 'WCF',
        title: '',
        referenceNumber: '',
        claimAmount: 0,
        status: 'submitted',
        incidentOrEventDate: new Date().toISOString().split('T')[0],
        expiryOrDueDate: '',
        notes: ''
      });
    } catch (err) {
      handleFirestoreError(err, OperationType.CREATE, 'compliance_records');
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadComplianceTemplate = () => {
    const headers = [
      'Employee Code or Email',
      'Employee Name',
      'NSSF Number',
      'NHIF Card Number',
      'WCF Reg Number',
      'OSHA Reg Number',
      'OSHA Exam Date',
      'OSHA Status',
      'HESLB Control Number',
      'HESLB Balance',
      'Claim Authority',
      'Claim Title',
      'Claim Reference',
      'Claim Amount',
      'Claim Status',
      'Event Date',
      'Notes'
    ];

    const sampleRows = [
      [
        'EMP001',
        'Jackson Mandela',
        '1029384756',
        '9988776655',
        'WCF-9901',
        'OSHA-2026-01',
        '2026-01-15',
        'compliant',
        'HESLB-100223',
        '1500000',
        'WCF',
        'Workplace Minor Injury Claim',
        'WCF-CLAIM-001',
        '350000',
        'submitted',
        '2026-06-10',
        'Medical refund submitted to WCF'
      ],
      [
        'sarah.j@brighttech.co.tz',
        'Sarah Joseph',
        '1029384757',
        '9988776656',
        'WCF-9902',
        'OSHA-2026-02',
        '2026-02-20',
        'pending_exam',
        '',
        '0',
        'OSHA',
        'Annual Medical Examination Scheduled',
        'OSHA-MED-2026',
        '0',
        'under_review',
        '2026-07-01',
        'Awaiting clinic medical feedback report'
      ]
    ];

    const csvContent = '\uFEFF' + Papa.unparse({ fields: headers, data: sampleRows });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadFile(blob, 'Compliance_Bulk_Upload_Template.csv');
    toast.success('Downloaded statutory compliance CSV template');
  };

  const handleComplianceCsvFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBulkComplianceFileName(file.name);
    setBulkUploadSummary(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (!results.data || results.data.length === 0) {
          toast.error('The selected CSV file contains no data rows');
          return;
        }

        const parsedRows: BulkComplianceRowPreview[] = [];

        results.data.forEach((row: any, idx: number) => {
          const findVal = (...keys: string[]) => {
            for (const k of keys) {
              const foundKey = Object.keys(row).find(
                rk => rk.trim().toLowerCase().replace(/[^a-z0-9]/g, '') === k.toLowerCase().replace(/[^a-z0-9]/g, '')
              );
              if (foundKey && row[foundKey] !== undefined && row[foundKey] !== null) {
                return String(row[foundKey]).trim();
              }
            }
            return '';
          };

          const rawIdentifier = findVal('employeecodeoremail', 'employeecode', 'code', 'email', 'employeeid', 'identifier');
          const employeeNameInput = findVal('employeename', 'fullname', 'name');

          let matched: Employee | undefined;
          if (rawIdentifier) {
            matched = employees.find(
              emp =>
                (emp.employeeCode && emp.employeeCode.toLowerCase() === rawIdentifier.toLowerCase()) ||
                (emp.email && emp.email.toLowerCase() === rawIdentifier.toLowerCase()) ||
                (emp.id && emp.id === rawIdentifier)
            );
          }

          if (!matched && employeeNameInput) {
            matched = employees.find(
              emp =>
                `${emp.firstName} ${emp.lastName}`.toLowerCase().trim() === employeeNameInput.toLowerCase().trim() ||
                emp.firstName.toLowerCase().trim() === employeeNameInput.toLowerCase().trim()
            );
          }

          const nssfNumber = findVal('nssfnumber', 'nssf', 'nssfno');
          const nhifNumber = findVal('nhifcardnumber', 'nhifnumber', 'nhif', 'nhifno');
          const wcfNumber = findVal('wcfregnumber', 'wcfnumber', 'wcf', 'wcfno');
          const oshaRegistrationNo = findVal('osharegnumber', 'osharegno', 'osha');
          const oshaMedicalExamDate = findVal('oshaexamdate', 'oshamedicalexamdate', 'oshadate');
          const rawOshaStatus = findVal('oshastatus').toLowerCase();
          let oshaStatus: 'compliant' | 'pending_exam' | 'expired' | undefined;
          if (['compliant', 'pending_exam', 'expired'].includes(rawOshaStatus)) {
            oshaStatus = rawOshaStatus as any;
          }

          const heslbControlNo = findVal('heslbcontrolnumber', 'heslbcontrolno', 'heslbref');
          const rawHeslbBal = findVal('heslbloanbalance', 'heslbbalance');
          const heslbBalance = rawHeslbBal ? parseFloat(rawHeslbBal) : undefined;

          const rawAuthority = findVal('claimauthority', 'authority', 'statutoryauthority').toUpperCase();
          let claimAuthority: any;
          if (['WCF', 'OSHA', 'NHIF', 'NSSF', 'SDL', 'HESLB'].includes(rawAuthority)) {
            claimAuthority = rawAuthority;
          } else if (rawAuthority) {
            claimAuthority = 'General';
          }

          const claimTitle = findVal('claimtitle', 'title', 'complianceitem');
          const claimRef = findVal('claimreference', 'referencenumber', 'claimref', 'ref');
          const rawClaimAmt = findVal('claimamount', 'amount');
          const claimAmount = rawClaimAmt ? parseFloat(rawClaimAmt) : undefined;
          const rawClaimStatus = findVal('claimstatus', 'status').toLowerCase();
          let claimStatus: any;
          if (['compliant', 'submitted', 'under_review', 'approved', 'paid', 'expired'].includes(rawClaimStatus)) {
            claimStatus = rawClaimStatus;
          } else if (claimTitle) {
            claimStatus = 'submitted';
          }

          const incidentDate = findVal('eventdate', 'incidentdate', 'date') || new Date().toISOString().split('T')[0];
          const notes = findVal('notes', 'remarks', 'description');

          parsedRows.push({
            rowIndex: idx + 1,
            rawIdentifier: rawIdentifier || employeeNameInput || `Row ${idx + 1}`,
            employeeNameInput,
            matchedEmployee: matched,
            matchError: !matched ? 'Employee not found by Code/Email/Name' : undefined,
            nssfNumber,
            nhifNumber,
            wcfNumber,
            oshaRegistrationNo,
            oshaMedicalExamDate,
            oshaStatus,
            heslbControlNo,
            heslbBalance,
            claimAuthority,
            claimTitle,
            claimRef,
            claimAmount,
            claimStatus,
            incidentDate,
            notes,
            selected: !!matched
          });
        });

        setBulkComplianceRows(parsedRows);
        toast.success(`Parsed ${parsedRows.length} compliance rows from CSV`);
      },
      error: (err) => {
        toast.error(`Error reading CSV file: ${err.message}`);
      }
    });
  };

  const processBulkComplianceUpload = async () => {
    const selectedRows = bulkComplianceRows.filter(r => r.selected && r.matchedEmployee);
    if (selectedRows.length === 0) {
      toast.error('No valid matched rows selected for upload');
      return;
    }

    setIsBulkUploading(true);
    let updatedProfiles = 0;
    let createdClaims = 0;
    let failedRows = 0;

    try {
      for (const row of selectedRows) {
        if (!row.matchedEmployee) continue;
        const emp = row.matchedEmployee;

        // 1. Update employee statutory fields
        const empUpdates: Record<string, any> = {};
        if (row.nssfNumber && row.nssfNumber !== emp.nssfNumber) empUpdates.nssfNumber = row.nssfNumber;
        if (row.nhifNumber && row.nhifNumber !== emp.nhifNumber) empUpdates.nhifNumber = row.nhifNumber;
        if (row.wcfNumber && row.wcfNumber !== emp.wcfNumber) empUpdates.wcfNumber = row.wcfNumber;
        if (row.oshaRegistrationNo && row.oshaRegistrationNo !== emp.oshaRegistrationNo) empUpdates.oshaRegistrationNo = row.oshaRegistrationNo;
        if (row.oshaMedicalExamDate && row.oshaMedicalExamDate !== emp.oshaMedicalExamDate) empUpdates.oshaMedicalExamDate = row.oshaMedicalExamDate;
        if (row.oshaStatus && row.oshaStatus !== emp.oshaStatus) empUpdates.oshaStatus = row.oshaStatus;
        if (row.heslbControlNo && row.heslbControlNo !== emp.heslbControlNo) empUpdates.heslbControlNo = row.heslbControlNo;
        if (row.heslbBalance !== undefined && !isNaN(row.heslbBalance)) {
          empUpdates.heslbBalance = row.heslbBalance;
          empUpdates.hasHeslbLoan = row.heslbBalance > 0;
        }

        if (Object.keys(empUpdates).length > 0) {
          try {
            await updateDoc(doc(db, 'employees', emp.id), empUpdates);
            updatedProfiles++;
          } catch (e) {
            console.error(`Failed to update employee ${emp.id}:`, e);
            failedRows++;
          }
        }

        // 2. Insert new compliance record/claim if claim info exists
        if (row.claimAuthority && row.claimTitle) {
          try {
            const claimData: Omit<ComplianceRecord, 'id'> = {
              employeeId: emp.id,
              employeeName: `${emp.firstName} ${emp.lastName}`,
              authority: row.claimAuthority,
              title: row.claimTitle,
              referenceNumber: row.claimRef || `BULK-${Date.now().toString().slice(-6)}`,
              claimAmount: row.claimAmount || 0,
              status: row.claimStatus || 'submitted',
              incidentOrEventDate: row.incidentDate || new Date().toISOString().split('T')[0],
              expiryOrDueDate: '',
              notes: row.notes || 'Imported via CSV Bulk Compliance Upload',
              createdAt: Timestamp.now()
            };
            await addDoc(collection(db, 'compliance_records'), claimData);
            createdClaims++;
          } catch (e) {
            console.error(`Failed to create compliance record for ${emp.id}:`, e);
            failedRows++;
          }
        }
      }

      await logAudit(
        'Bulk Statutory Compliance CSV Import',
        `Bulk updated ${updatedProfiles} employee statutory profiles and created ${createdClaims} compliance records/claims via CSV mapping.`,
        format(new Date(), 'yyyy-MM')
      );

      setBulkUploadSummary({
        updatedProfiles,
        createdClaims,
        failedRows
      });

      toast.success(`Bulk upload finished! ${updatedProfiles} profiles updated, ${createdClaims} claims created.`);
    } catch (err) {
      console.error('Bulk upload error:', err);
      toast.error('An error occurred during bulk compliance upload');
    } finally {
      setIsBulkUploading(false);
    }
  };

  const deleteEmployee = async (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Employee',
      message: 'Are you sure you want to delete this employee? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'employees', id));
          toast.success('Employee deleted');
        } catch (error) {
          toast.error('Failed to delete');
        }
      }
    });
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">HR Dashboard</h1>
          <p className="text-slate-500 font-medium italic">Manage employees and payroll</p>
        </div>
        <div className="flex items-center gap-3">
          <GenerateReportTrigger />
          {activeTab === 'employees' && canManageHR && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleDownloadEmployeeTemplate}
                className="flex items-center gap-2 px-3.5 py-3 bg-white border border-slate-200 text-slate-700 rounded-2xl font-bold hover:bg-slate-50 transition-all shadow-xs text-xs"
                title="Download Excel Template for importing employees"
              >
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                <span>Download Template</span>
              </button>
              <button
                onClick={() => employeeFileInputRef.current?.click()}
                className="flex items-center gap-2 px-3.5 py-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl font-bold hover:bg-emerald-100 transition-all shadow-xs text-xs"
                title="Import Employees from Excel File"
              >
                <Upload className="w-4 h-4 text-emerald-700" />
                <span>Import Excel</span>
              </button>
              <input
                type="file"
                ref={employeeFileInputRef}
                onChange={handleEmployeeExcelFileUpload}
                accept=".xlsx, .xls, .csv"
                className="hidden"
              />
              <button
                onClick={() => { setIsSubmitting(false); setIsEmployeeModalOpen(true); }}
                className="flex items-center gap-2 px-5 py-3 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 text-xs"
              >
                <Plus className="w-4 h-4" /> Add Employee
              </button>
            </div>
          )}
          {activeTab === 'deductions' && canManageHR && (
            <button onClick={() => {
              setIsSubmitting(false);
              setEditingDeduction(null);
              setDeductionForm({
                name: '',
                description: '',
                type: 'fixed',
                value: 0,
                categoryKind: 'loan',
                totalPrincipal: 0,
                totalReturn: 0,
                remainingBalance: 0,
                monthlyInstallment: 0,
                loanDate: new Date().toISOString().split('T')[0],
                durationMonths: 12,
                interestRate: 0,
                monthsPaid: 0,
                assignedEmployeeIds: []
              });
              setIsDeductionModalOpen(true);
            }} className="flex items-center gap-2 px-6 py-3 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200">
              <Plus className="w-5 h-5" /> Add Category
            </button>
          )}
          {activeTab === 'payroll' && canManageHR && (
            <div className="flex items-center gap-3">
              <button onClick={() => { setIsSubmitting(false); setIsMcColnCalculatorOpen(true); }} className="flex items-center gap-2 px-5 py-3 bg-white border border-slate-200 text-blue-800 rounded-2xl font-bold hover:bg-slate-50 transition-all shadow-sm">
                <Calculator className="w-5 h-5" /> MCColn Salary Calculator
              </button>
              <button onClick={() => {
                setIsSubmitting(false);
                const initialCustomDeductions: Record<string, string[]> = {};
                employees.forEach(emp => {
                  initialCustomDeductions[emp.id] = deductionCategories
                    .filter(cat => cat.assignedEmployeeIds && cat.assignedEmployeeIds.includes(emp.id))
                    .map(cat => cat.id);
                });
                setPayrollForm({
                  employeeIds: employees.map(e => e.id),
                  month: format(new Date(), 'yyyy-MM'),
                  deductions: [],
                  employeeCustomDeductions: initialCustomDeductions,
                  employeeTaxResidency: {},
                  employeeTaxExempt: {},
                  employeeStatutoryOpt: {}
                });
                setIsPayrollModalOpen(true);
              }} className="flex items-center gap-2 px-6 py-3 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200">
                <Plus className="w-5 h-5" /> Generate Payroll
              </button>
            </div>
          )}
          {activeTab === 'compliance' && canManageHR && (
            <button 
              onClick={() => {
                setIsSubmitting(false);
                setComplianceForm({
                  employeeId: employees[0]?.id || '',
                  authority: 'WCF',
                  title: '',
                  referenceNumber: '',
                  claimAmount: 0,
                  status: 'submitted',
                  incidentOrEventDate: new Date().toISOString().split('T')[0],
                  expiryOrDueDate: '',
                  notes: ''
                });
                setIsComplianceModalOpen(true);
              }} 
              className="flex items-center gap-2 px-6 py-3 bg-emerald-700 text-white rounded-2xl font-bold hover:bg-emerald-800 transition-all shadow-lg shadow-emerald-200"
            >
              <Plus className="w-5 h-5" /> Launch Claim / Record
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 p-1 bg-slate-100 rounded-2xl w-fit flex-wrap">
        <button onClick={() => setActiveTab('employees')} className={cn("px-6 py-2.5 rounded-xl text-sm font-bold transition-all", activeTab === 'employees' ? "bg-white text-blue-800 shadow-sm" : "text-slate-600 hover:text-slate-900")}>Employees</button>
        <button onClick={() => setActiveTab('payroll')} className={cn("px-6 py-2.5 rounded-xl text-sm font-bold transition-all", activeTab === 'payroll' ? "bg-white text-blue-800 shadow-sm" : "text-slate-600 hover:text-slate-900")}>Payroll</button>
        <button onClick={() => setActiveTab('deductions')} className={cn("px-6 py-2.5 rounded-xl text-sm font-bold transition-all", activeTab === 'deductions' ? "bg-white text-blue-800 shadow-sm" : "text-slate-600 hover:text-slate-900")}>Deductions & Loans</button>
        <button onClick={() => setActiveTab('compliance')} className={cn("px-6 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2", activeTab === 'compliance' ? "bg-white text-blue-800 shadow-sm" : "text-slate-600 hover:text-slate-900")}>
          <ShieldCheck className="w-4 h-4 text-emerald-600" /> Government Compliance & Claims
        </button>
        <button onClick={() => setActiveTab('attendance')} className={cn("px-6 py-2.5 rounded-xl text-sm font-bold transition-all", activeTab === 'attendance' ? "bg-white text-blue-800 shadow-sm" : "text-slate-600 hover:text-slate-900")}>Attendance & Logs</button>
      </div>

      {activeTab === 'employees' && (
        <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-50 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="relative max-w-md w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input type="text" placeholder="Search employees..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Category:</span>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-xl text-xs font-bold text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-800/20"
                >
                  <option value="ALL">All Categories ({employees.length})</option>
                  {EMPLOYEE_CATEGORIES.map((cat, idx) => {
                    const count = employees.filter(e => getEmployeeCategory(e).toLowerCase() === cat.toLowerCase()).length;
                    return (
                      <option key={`cat-opt-${cat}-${idx}`} value={cat}>{cat} ({count})</option>
                    );
                  })}
                </select>
              </div>
            </div>

            {/* Category Filter Pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
              <button
                onClick={() => setCategoryFilter('ALL')}
                className={cn(
                  "px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all",
                  categoryFilter === 'ALL' ? "bg-blue-900 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                )}
              >
                All Staff ({employees.length})
              </button>
              {EMPLOYEE_CATEGORIES.map((cat, idx) => {
                const count = employees.filter(e => getEmployeeCategory(e).toLowerCase() === cat.toLowerCase()).length;
                if (count === 0 && categoryFilter !== cat) return null;
                return (
                  <button
                    key={`cat-pill-${cat}-${idx}`}
                    onClick={() => setCategoryFilter(cat)}
                    className={cn(
                      "px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5",
                      categoryFilter === cat ? "bg-blue-900 text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    )}
                  >
                    <span>{cat}</span>
                    <span className={cn("px-1.5 py-0.5 rounded-full text-[10px]", categoryFilter === cat ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700")}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left bg-slate-50/50">
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Name</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Category</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Department</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Position</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Salary</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Status</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredEmployees.map((emp, idx) => (
                  <tr key={emp.id ? `emp-row-${emp.id}` : `emp-idx-${idx}`} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-800 font-bold">{emp.firstName[0]}{emp.lastName[0]}</div>
                        <div>
                          <p className="font-bold text-slate-900">{emp.firstName} {emp.lastName}</p>
                          <p className="text-xs text-slate-500">{emp.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider bg-blue-50 text-blue-800 border border-blue-100">
                        {getEmployeeCategory(emp)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-medium">{emp.department}</td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-medium">{emp.position}</td>
                    <td className="px-6 py-4 text-sm font-black text-slate-900">TZS {emp.salary.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className={cn("px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest", emp.status === 'active' ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500")}>{emp.status}</span>
                    </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => {
                            setEditingEmployee(emp);
                            setEmployeeForm({
                              employeeCode: emp.employeeCode || '',
                              firstName: emp.firstName,
                              lastName: emp.lastName,
                              email: emp.email,
                              phone: emp.phone,
                              department: emp.department,
                              position: emp.position,
                              category: emp.category || getEmployeeCategory(emp),
                              salary: emp.salary,
                              tin: emp.tin || '',
                              nssfNumber: emp.nssfNumber || '',
                              nhifNumber: emp.nhifNumber || '',
                              wcfNumber: emp.wcfNumber || '',
                              oshaRegistrationNo: emp.oshaRegistrationNo || '',
                              oshaMedicalExamDate: emp.oshaMedicalExamDate || '',
                              oshaStatus: emp.oshaStatus || 'compliant',
                              heslbControlNo: emp.heslbControlNo || '',
                              heslbBalance: emp.heslbBalance || 0,
                              bankName: emp.bankName || '',
                              accountNumber: emp.accountNumber || '',
                              joiningDate: emp.joiningDate ? format(emp.joiningDate.toDate(), 'yyyy-MM-dd') : new Date().toISOString().split('T')[0],
                              status: emp.status,
                              hasHeslbLoan: emp.hasHeslbLoan || false,
                              taxResidency: emp.taxResidency || 'resident',
                              isTaxExempt: emp.isTaxExempt || false,
                              allowances: emp.allowances || []
                            });
                            setIsEmployeeModalOpen(true);
                          }} className="p-2 text-slate-400 hover:text-blue-600 transition-colors"><Edit2 className="w-4 h-4" /></button>
                          {canManageHR && (
                            <button onClick={() => deleteEmployee(emp.id)} className="p-2 text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                          )}
                        </div>
                      </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'deductions' && (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between bg-slate-900 text-white p-6 rounded-[2rem] shadow-sm gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-500/20 text-red-400 rounded-2xl flex items-center justify-center font-bold">
                <CreditCard className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold tracking-tight">Deductions & Employee Loan Management</h2>
                <p className="text-xs text-slate-400 font-medium">Create and track employee loans, salary advances, union dues, and custom deductions</p>
              </div>
            </div>
            {canManageHR && (
              <button
                onClick={() => {
                  setEditingDeduction(null);
                  setDeductionForm({
                    name: '',
                    description: '',
                    type: 'fixed',
                    value: 0,
                    categoryKind: 'loan',
                    totalPrincipal: 0,
                    totalReturn: 0,
                    remainingBalance: 0,
                    monthlyInstallment: 0,
                    loanDate: new Date().toISOString().split('T')[0],
                    durationMonths: 12,
                    interestRate: 0,
                    monthsPaid: 0,
                    assignedEmployeeIds: []
                  });
                  setIsDeductionModalOpen(true);
                }}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-xl flex items-center gap-2 transition-all shadow-md shrink-0"
              >
                <Plus className="w-4 h-4" /> Add Loan / Deduction Category
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deductionCategories.length === 0 ? (
              <div className="col-span-3 bg-white p-12 rounded-[2.5rem] border border-slate-100 text-center space-y-3">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto text-slate-400">
                  <CreditCard className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-slate-800">No Deduction Categories Created</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">Create loan deduction rules, salary advance trackers, or union fees to automatically apply them to payroll.</p>
              </div>
            ) : (
              deductionCategories.map(cat => {
                const isLoan = cat.categoryKind === 'loan' || cat.categoryKind === 'advance';
                const principal = cat.totalPrincipal || 0;
                const balance = cat.remainingBalance || 0;
                const progressPct = principal > 0 ? Math.max(0, Math.min(100, Math.round(((principal - balance) / principal) * 100))) : 0;

                return (
                  <div key={cat.id} className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-red-50 rounded-2xl flex items-center justify-center text-red-600 font-bold">
                            <CreditCard className="w-5 h-5" />
                          </div>
                          <span className="px-2.5 py-1 bg-slate-100 text-slate-700 font-black text-[10px] uppercase tracking-wider rounded-lg">
                            {cat.categoryKind ? cat.categoryKind.replace('_', ' ') : 'General'}
                          </span>
                        </div>
                        {canManageHR && (
                          <div className="flex gap-1">
                            <button onClick={() => {
                              setEditingDeduction(cat);
                              setDeductionForm({
                                name: cat.name,
                                description: cat.description || '',
                                type: cat.type || 'fixed',
                                value: cat.value || 0,
                                categoryKind: cat.categoryKind || 'general',
                                totalPrincipal: cat.totalPrincipal || 0,
                                totalReturn: cat.totalReturn || 0,
                                remainingBalance: cat.remainingBalance || 0,
                                monthlyInstallment: cat.monthlyInstallment || 0,
                                loanDate: cat.loanDate || new Date().toISOString().split('T')[0],
                                durationMonths: cat.durationMonths || 12,
                                interestRate: cat.interestRate || 0,
                                monthsPaid: cat.monthsPaid || 0,
                                assignedEmployeeIds: cat.assignedEmployeeIds || []
                              });
                              setIsDeductionModalOpen(true);
                            }} className="p-2 text-slate-400 hover:text-blue-600 transition-colors"><Edit2 className="w-4 h-4" /></button>
                            <button onClick={() => {
                              setConfirmModal({
                                isOpen: true,
                                title: 'Delete Deduction Category',
                                message: `Are you sure you want to delete the deduction category "${cat.name}"?`,
                                onConfirm: async () => {
                                  try {
                                    await deleteDoc(doc(db, 'deduction_categories', cat.id));
                                    toast.success('Deduction category deleted');
                                  } catch (error) {
                                    toast.error('Failed to delete deduction category');
                                  }
                                }
                              });
                            }} className="p-2 text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        )}
                      </div>

                      <h3 className="text-lg font-bold text-slate-900 mb-1">{cat.name}</h3>
                      <p className="text-xs text-slate-500 mb-4 line-clamp-2">{cat.description || 'No detailed description provided.'}</p>

                      {isLoan && principal > 0 && (
                        <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 mb-4 space-y-2 text-xs">
                          <div className="flex justify-between text-slate-600">
                            <span>Total Loan Principal:</span>
                            <span className="font-bold text-slate-900">TZS {principal.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-slate-600">
                            <span>Remaining Balance:</span>
                            <span className="font-bold text-red-600">TZS {balance.toLocaleString()}</span>
                          </div>
                          <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden mt-1">
                            <div className="bg-emerald-500 h-full rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
                          </div>
                          <div className="flex justify-between text-[10px] text-slate-400 font-bold uppercase tracking-wider pt-0.5">
                            <span>{progressPct}% Repaid</span>
                            <span>TZS {(cat.monthlyInstallment || cat.value || 0).toLocaleString()}/mo</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between border-t border-slate-100 pt-4 text-xs">
                      <span className="font-bold text-slate-700">
                        Monthly Deduction: <span className="text-red-600 font-black">{cat.type === 'percentage' ? `${cat.value}%` : `TZS ${cat.value?.toLocaleString()}`}</span>
                      </span>
                      <span className="px-2.5 py-0.5 bg-blue-50 text-blue-700 font-bold rounded-full text-[10px]">
                        {cat.assignedEmployeeIds?.length || 0} Employee(s)
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {activeTab === 'compliance' && (
        <div className="space-y-8">
          {/* Statutory Bodies Header Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden">
              <div className="w-8 h-8 bg-emerald-50 text-emerald-700 rounded-xl flex items-center justify-center mb-3">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-black uppercase text-emerald-700 tracking-wider">WCF Fund</span>
              <h4 className="text-base font-black text-slate-900 mt-1">0.5% Employer</h4>
              <p className="text-[11px] text-slate-500 font-medium mt-1">Workers Comp</p>
            </div>

            <div className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden">
              <div className="w-8 h-8 bg-blue-50 text-blue-700 rounded-xl flex items-center justify-center mb-3">
                <HeartPulse className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-black uppercase text-blue-700 tracking-wider">OSHA Safety</span>
              <h4 className="text-base font-black text-slate-900 mt-1">Medical Exams</h4>
              <p className="text-[11px] text-slate-500 font-medium mt-1">Annual Inspection</p>
            </div>

            <div className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden">
              <div className="w-8 h-8 bg-indigo-50 text-indigo-700 rounded-xl flex items-center justify-center mb-3">
                <Award className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-black uppercase text-indigo-700 tracking-wider">NHIF Health</span>
              <h4 className="text-base font-black text-slate-900 mt-1">3% Employee</h4>
              <p className="text-[11px] text-slate-500 font-medium mt-1">Health Insurance</p>
            </div>

            <div className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden">
              <div className="w-8 h-8 bg-teal-50 text-teal-700 rounded-xl flex items-center justify-center mb-3">
                <Landmark className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-black uppercase text-teal-700 tracking-wider">NSSF Pension</span>
              <h4 className="text-base font-black text-slate-900 mt-1">10% + 10%</h4>
              <p className="text-[11px] text-slate-500 font-medium mt-1">Social Security</p>
            </div>

            <div className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden">
              <div className="w-8 h-8 bg-amber-50 text-amber-700 rounded-xl flex items-center justify-center mb-3">
                <Briefcase className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-black uppercase text-amber-700 tracking-wider">SDL Levy</span>
              <h4 className="text-base font-black text-slate-900 mt-1">3.5% Skills</h4>
              <p className="text-[11px] text-slate-500 font-medium mt-1">Development Levy</p>
            </div>

            <div className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden">
              <div className="w-8 h-8 bg-rose-50 text-rose-700 rounded-xl flex items-center justify-center mb-3">
                <DollarSign className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-black uppercase text-rose-700 tracking-wider">HESLB Loan</span>
              <h4 className="text-base font-black text-slate-900 mt-1">15% Repayment</h4>
              <p className="text-[11px] text-slate-500 font-medium mt-1">Higher Ed Loans</p>
            </div>
          </div>

          {/* Compliance Register Table */}
          <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-6 space-y-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" /> Employee Statutory Compliance & Claims Register
                </h3>
                <p className="text-xs text-slate-500">Feed and launch compliance records, WCF injury claims, OSHA medical inspection statuses, and NHIF card updates for all staff.</p>
              </div>

              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                <div className="relative flex-1 md:w-48">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search staff or IDs..."
                    value={complianceSearch}
                    onChange={(e) => setComplianceSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>

                <select
                  value={complianceAuthorityFilter}
                  onChange={(e) => setComplianceAuthorityFilter(e.target.value as any)}
                  className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 cursor-pointer"
                >
                  <option value="all">All Statutory Bodies</option>
                  <option value="WCF">WCF (Workers Comp)</option>
                  <option value="OSHA">OSHA (Occupational Safety)</option>
                  <option value="NHIF">NHIF (Health Insurance)</option>
                  <option value="NSSF">NSSF (Pension Fund)</option>
                  <option value="SDL">SDL (Skills Levy)</option>
                  <option value="HESLB">HESLB (Higher Ed Loans)</option>
                </select>

                {canManageHR && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={downloadComplianceTemplate}
                      className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors flex items-center gap-1.5 border border-slate-200"
                      title="Download sample CSV template"
                    >
                      <Download className="w-3.5 h-3.5 text-slate-500" />
                      <span>Template</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setBulkComplianceRows([]);
                        setBulkComplianceFileName('');
                        setBulkUploadSummary(null);
                        setIsBulkComplianceModalOpen(true);
                      }}
                      className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs transition-colors flex items-center gap-1.5 shadow-sm"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      <span>Bulk Upload CSV</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Employees Compliance Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50/80 text-slate-600 uppercase font-bold text-[10px] tracking-wider">
                  <tr>
                    <th className="p-4 rounded-l-2xl">Employee</th>
                    <th className="p-4">NSSF / NHIF Nos.</th>
                    <th className="p-4">WCF Reg No.</th>
                    <th className="p-4">OSHA Med Exam Status</th>
                    <th className="p-4">HESLB Control & Status</th>
                    <th className="p-4">Active Claims</th>
                    <th className="p-4 text-right rounded-r-2xl">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs font-medium">
                  {employees
                    .filter(emp => {
                      const searchStr = `${emp.firstName} ${emp.lastName} ${emp.department} ${emp.nssfNumber || ''} ${emp.nhifNumber || ''} ${emp.wcfNumber || ''}`.toLowerCase();
                      return searchStr.includes(complianceSearch.toLowerCase());
                    })
                    .map(emp => {
                      const empClaims = complianceRecords.filter(c => c.employeeId === emp.id && (complianceAuthorityFilter === 'all' || c.authority === complianceAuthorityFilter));
                      
                      return (
                        <tr key={emp.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-emerald-50 text-emerald-700 font-bold rounded-xl flex items-center justify-center shrink-0">
                                {emp.firstName[0]}{emp.lastName[0]}
                              </div>
                              <div>
                                <p className="font-bold text-slate-900">{emp.firstName} {emp.lastName}</p>
                                <p className="text-[10px] text-slate-500 font-mono">{emp.employeeCode || emp.department}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 space-y-0.5">
                            <p className="text-[11px] text-slate-700">NSSF: <span className="font-bold font-mono">{emp.nssfNumber || 'Not Fed'}</span></p>
                            <p className="text-[11px] text-slate-700">NHIF: <span className="font-bold font-mono">{emp.nhifNumber || 'Not Fed'}</span></p>
                          </td>
                          <td className="p-4">
                            <span className="font-mono font-bold text-slate-800">{emp.wcfNumber || 'WCF-REGISTERED'}</span>
                          </td>
                          <td className="p-4">
                            <span className={cn(
                              "px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider",
                              emp.oshaStatus === 'compliant' ? "bg-emerald-50 text-emerald-700" :
                              emp.oshaStatus === 'pending_exam' ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"
                            )}>
                              {emp.oshaStatus ? emp.oshaStatus.replace('_', ' ') : 'Compliant'}
                            </span>
                          </td>
                          <td className="p-4">
                            {emp.hasHeslbLoan ? (
                              <div className="space-y-0.5">
                                <span className="px-2 py-0.5 bg-rose-50 text-rose-700 font-bold rounded text-[10px]">15% Active Loan</span>
                                <p className="text-[10px] text-slate-500 font-mono">Ref: {emp.heslbControlNo || '10029381'}</p>
                              </div>
                            ) : (
                              <span className="text-slate-400 text-xs font-semibold">No HESLB Loan</span>
                            )}
                          </td>
                          <td className="p-4">
                            <span className="px-2.5 py-1 bg-slate-100 text-slate-800 font-bold text-[10px] rounded-lg">
                              {empClaims.length} Claim(s) Logged
                            </span>
                          </td>
                          <td className="p-4 text-right">
                            {canManageHR && (
                              <button
                                onClick={() => {
                                  setComplianceForm({
                                    employeeId: emp.id,
                                    authority: 'WCF',
                                    title: `WCF Claim for ${emp.firstName} ${emp.lastName}`,
                                    referenceNumber: `WCF-${Date.now().toString().slice(-6)}`,
                                    claimAmount: 0,
                                    status: 'submitted',
                                    incidentOrEventDate: new Date().toISOString().split('T')[0],
                                    expiryOrDueDate: '',
                                    notes: ''
                                  });
                                  setIsComplianceModalOpen(true);
                                }}
                                className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold rounded-xl text-xs transition-colors border border-emerald-200"
                              >
                                + Launch Claim
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Compliance Claim Logs & Historical Submissions */}
            <div className="border-t border-slate-100 pt-6">
              <h4 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-800" /> Historical Compliance Claims & Statutory Filings Log
              </h4>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 font-bold uppercase text-[10px]">
                    <tr>
                      <th className="p-3">Event Date</th>
                      <th className="p-3">Employee</th>
                      <th className="p-3">Authority</th>
                      <th className="p-3">Claim / Filing Title</th>
                      <th className="p-3">Ref No.</th>
                      <th className="p-3 text-right">Claim Amount</th>
                      <th className="p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                    {complianceRecords.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-6 text-center text-slate-400">
                          No compliance claims or statutory entries logged yet. Click "Launch Claim / Record" to record a new WCF, OSHA, or NHIF entry.
                        </td>
                      </tr>
                    ) : (
                      complianceRecords
                        .filter(c => complianceAuthorityFilter === 'all' || c.authority === complianceAuthorityFilter)
                        .map(c => (
                          <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                            <td className="p-3 font-mono text-slate-600">{c.incidentOrEventDate || '—'}</td>
                            <td className="p-3 font-bold text-slate-900">{c.employeeName}</td>
                            <td className="p-3">
                              <span className="px-2 py-0.5 bg-blue-50 text-blue-800 font-black rounded text-[10px]">
                                {c.authority}
                              </span>
                            </td>
                            <td className="p-3 font-semibold text-slate-800">{c.title}</td>
                            <td className="p-3 font-mono text-slate-500">{c.referenceNumber || 'N/A'}</td>
                            <td className="p-3 text-right font-bold text-emerald-600">
                              {(c.claimAmount ?? 0) > 0 ? `TZS ${(c.claimAmount ?? 0).toLocaleString()}` : '—'}
                            </td>
                            <td className="p-3">
                              <span className={cn(
                                "px-2 py-0.5 rounded font-black text-[10px] uppercase tracking-wider",
                                c.status === 'approved' || c.status === 'paid' || c.status === 'compliant' ? "bg-emerald-100 text-emerald-800" :
                                c.status === 'submitted' || c.status === 'under_review' ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"
                              )}>
                                {c.status}
                              </span>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'payroll' && (
        <div className="space-y-6">
          {/* Visual Analytics Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-800" /> Monthly Payroll Expenditure Trends (TZS)
              </h3>
              <p className="text-xs text-slate-500 mb-6">Gross Pay vs Net Payable vs Employer Cost across months</p>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={Array.from(new Set(payrolls.map(p => p.month))).sort().map(m => {
                    const mp = payrolls.filter(p => p.month === m);
                    return {
                      month: m,
                      Gross: mp.reduce((s, p) => s + p.grossPay, 0),
                      Net: mp.reduce((s, p) => s + p.netSalary, 0),
                      EmployerCost: mp.reduce((s, p) => s + p.nssfEmployer + p.sdl + p.wcf + p.nhifEmployer, 0)
                    };
                  })}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} tickLine={false} />
                    <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} tickFormatter={(v) => `${v >= 1000000 ? (v/1000000).toFixed(1) + 'M' : v}`} />
                    <Tooltip formatter={(value: any) => [`TZS ${Number(value).toLocaleString()}`, '']} />
                    <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                    <Bar dataKey="Gross" fill="#1e40af" radius={[6, 6, 0, 0]} name="Gross Pay" />
                    <Bar dataKey="Net" fill="#059669" radius={[6, 6, 0, 0]} name="Net Salary" />
                    <Bar dataKey="EmployerCost" fill="#4f46e5" radius={[6, 6, 0, 0]} name="Employer Cost" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-600" /> Department-Wise Salary Distribution
              </h3>
              <p className="text-xs text-slate-500 mb-6">Total payroll expenditure breakdown by department</p>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    {(() => {
                      const deptMap: Record<string, number> = {};
                      payrolls.forEach(p => {
                        deptMap[p.department || 'General'] = (deptMap[p.department || 'General'] || 0) + p.grossPay;
                      });
                      const data = Object.keys(deptMap).map(d => ({ name: d, value: deptMap[d] }));
                      const COLORS = ['#1e40af', '#059669', '#d97706', '#4f46e5', '#e11d48', '#0891b2'];
                      return (
                        <>
                          <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            dataKey="value"
                            label={({ name, percent }) => `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                            labelLine={false}
                          >
                            {data.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: any) => [`TZS ${Number(value).toLocaleString()}`, 'Gross Salary']} />
                        </>
                      );
                    })()}
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Batch Operations & CSV Toolbar */}
          {(() => {
            const availableMonths = Array.from(new Set(filteredPayrolls.map(p => p.month))).sort((a, b) => (b as string).localeCompare(a as string));
            const allSelected = availableMonths.length > 0 && availableMonths.every(m => selectedBatchMonths.includes(m));

            return (
              <div className="bg-slate-900 text-white p-5 rounded-[2rem] shadow-sm mb-6 flex flex-wrap items-center justify-between gap-4 border border-slate-800">
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-xs font-bold cursor-pointer hover:text-teal-300 transition-colors select-none">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedBatchMonths(availableMonths);
                        } else {
                          setSelectedBatchMonths([]);
                        }
                      }}
                      className="w-4 h-4 rounded border-slate-700 text-teal-500 focus:ring-teal-500 cursor-pointer"
                    />
                    <span>Select All Batches ({availableMonths.length})</span>
                  </label>

                  {selectedBatchMonths.length > 0 && (
                    <span className="px-3 py-1 bg-teal-500/20 text-teal-300 rounded-xl text-xs font-bold border border-teal-500/30">
                      {selectedBatchMonths.length} Selected
                    </span>
                  )}

                  {/* Salary Range Filter Dropdown */}
                  <div className="flex items-center gap-2 bg-slate-800 text-white px-3 py-1.5 rounded-xl border border-slate-700">
                    <Filter className="w-3.5 h-3.5 text-teal-400 shrink-0" />
                    <span className="text-[11px] font-bold text-slate-300">Salary Filter:</span>
                    <select
                      value={salaryRangeFilter}
                      onChange={(e) => setSalaryRangeFilter(e.target.value as any)}
                      className="bg-slate-900 text-white text-xs font-bold py-1 px-2.5 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                    >
                      <option value="all">All Salaries</option>
                      <option value="under1m">Under 1,000,000 TZS</option>
                      <option value="1m_to_3m">1,000,000 - 3,000,000 TZS</option>
                      <option value="above3m">Above 3,000,000 TZS</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {selectedBatchMonths.length > 0 && canManageHR && (
                    <button
                      onClick={() => {
                        const selectedPayrolls = filteredPayrolls.filter(p => selectedBatchMonths.includes(p.month) && p.status !== 'paid');
                        const totalRecords = selectedPayrolls.length;
                        setConfirmModal({
                          isOpen: true,
                          title: 'Mark Selected Batches as Paid',
                          message: `Are you sure you want to finalize and mark all ${totalRecords} pending salary slip(s) across ${selectedBatchMonths.length} selected payroll batch(es) (${selectedBatchMonths.join(', ')}) as Paid?`,
                          onConfirm: async () => {
                            setConfirmModal(prev => ({ ...prev, isOpen: false }));
                            const batch = writeBatch(db);
                            const emailPromises = [];
                            for (const p of selectedPayrolls) {
                              batch.update(doc(db, 'payrolls', p.id), { status: 'paid', updatedAt: Timestamp.now() });
                              emailPromises.push(sendSalarySlipEmail(p));
                            }
                            await batch.commit();
                            Promise.all(emailPromises).catch(console.error); // Send emails async without blocking
                            
                            await logAudit('MARK_BULK_BATCHES_PAID', `Finalized and marked ${selectedBatchMonths.length} payroll batches (${totalRecords} employee records) as Paid`, selectedBatchMonths.join(', '));
                            setSelectedBatchMonths([]);
                            toast.success(`Marked ${totalRecords} payroll record(s) across ${selectedBatchMonths.length} batch(es) as Paid!`);
                          }
                        });
                      }}
                      className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-lg shadow-emerald-600/20 transition-all cursor-pointer"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Mark {selectedBatchMonths.length} Batch(es) Paid
                    </button>
                  )}

                  <button
                    onClick={() => setIsExportModalOpen(true)}
                    className="px-4 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-lg shadow-emerald-700/20 transition-all cursor-pointer"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Export Excel (Opt Deductions)
                  </button>

                  <button
                    onClick={() => {
                      const recordsToExport = selectedBatchMonths.length > 0
                        ? filteredPayrolls.filter(p => selectedBatchMonths.includes(p.month))
                        : filteredPayrolls;
                      const periodLabel = selectedBatchMonths.length > 0 ? selectedBatchMonths.join('_') : 'Active_Period';
                      exportPayrollCSV(recordsToExport, periodLabel);
                    }}
                    className="px-4 py-2.5 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-lg shadow-blue-700/20 transition-all cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    {selectedBatchMonths.length > 0 ? `Download CSV (${selectedBatchMonths.length} Batches)` : 'Quick CSV Export'}
                  </button>
                  <button
                    onClick={() => {
                      const tid = toast.loading('Re-syncing payroll records with Firestore...');
                      setTimeout(() => {
                        toast.dismiss(tid);
                        toast.success('Payroll data successfully re-synchronized with cloud database.');
                      }, 600);
                    }}
                    className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm transition-all cursor-pointer"
                    title="Re-sync data with cloud"
                  >
                    <RefreshCw className="w-4 h-4 text-blue-600" />
                    Re-Sync
                  </button>
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from(new Set(filteredPayrolls.map(p => p.month)))
              .sort((a, b) => (b as string).localeCompare(a as string))
              .map((month, index) => {
                const monthPayrolls = filteredPayrolls.filter(p => p.month === month);
                const totalNet = monthPayrolls.reduce((sum, p) => sum + p.netSalary, 0);
                const totalDeductions = monthPayrolls.reduce((sum, p) => sum + p.totalDeductions, 0);
                const totalEmployerCost = monthPayrolls.reduce((sum, p) => sum + (p.nssfEmployer + p.sdl + p.wcf + p.nhifEmployer), 0);
                const paidCount = monthPayrolls.filter(p => p.status === 'paid').length;
                const isBatchSelected = selectedBatchMonths.includes(month);

                return (
                  <motion.div
                    key={month}
                    initial={{ opacity: 0, y: 20, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    whileHover={{ y: -6, scale: 1.015, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.08), 0 8px 10px -6px rgba(0,0,0,0.02)' }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.25, ease: "easeOut", delay: index * 0.04 }}
                    className={cn(
                      "bg-white p-6 rounded-[2.5rem] border transition-all group relative cursor-pointer",
                      isBatchSelected ? "border-teal-500 ring-2 ring-teal-500/20 bg-teal-50/20" : "border-slate-100 shadow-sm hover:border-slate-200"
                    )}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {canManageHR && (
                          <input
                            type="checkbox"
                            checked={isBatchSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (e.target.checked) {
                                setSelectedBatchMonths(prev => [...prev, month]);
                              } else {
                                setSelectedBatchMonths(prev => prev.filter(m => m !== month));
                              }
                            }}
                            className="w-5 h-5 rounded border-slate-300 text-teal-700 focus:ring-teal-700 cursor-pointer shrink-0"
                            title="Select batch for bulk operation"
                          />
                        )}
                        <div className="w-10 h-10 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-800">
                          <Calendar className="w-5 h-5" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest",
                          paidCount === monthPayrolls.length ? "bg-emerald-50 text-emerald-600" : "bg-orange-50 text-orange-600"
                        )}>
                          {paidCount}/{monthPayrolls.length} Paid
                        </span>
                        {canManageHR && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmModal({
                                isOpen: true,
                                title: 'Delete Payroll',
                                message: `Are you sure you want to delete ALL payroll records for ${month}?`,
                                onConfirm: async () => {
                                  try {
                                    setConfirmModal(prev => ({ ...prev, isOpen: false }));
                                    await Promise.all(monthPayrolls.map(p => deleteDoc(doc(db, 'payrolls', p.id))));
                                    toast.success(`Payroll for ${month} deleted`);
                                  } catch (error) {
                                    toast.error('Failed to delete payroll');
                                  }
                                }
                              });
                            }}
                            className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <h3 className="text-xl font-black text-slate-900 mb-1">Payroll for {month}</h3>
                    <p className="text-sm text-slate-500 font-medium mb-4">{monthPayrolls.length} Employees</p>
                    
                    <div className="space-y-2 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Net Payable:</span>
                        <span className="font-bold text-emerald-600">TZS {totalNet.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Govt Deductions:</span>
                        <span className="font-bold text-red-600">TZS {totalDeductions.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Employer Cost:</span>
                        <span className="font-bold text-blue-600">TZS {totalEmployerCost.toLocaleString()}</span>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        setSelectedMonth(month);
                        setIsPayrollDetailsModalOpen(true);
                      }}
                      className="w-full py-3 bg-slate-50 text-slate-900 rounded-2xl font-bold text-sm hover:bg-slate-100 transition-all flex items-center justify-center gap-2"
                    >
                      <Eye className="w-4 h-4" /> View Details
                    </button>
                  </motion.div>
                );
              })}
          </div>

          {/* Audit Trail Section */}
          <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm mt-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-50 text-indigo-700 rounded-2xl flex items-center justify-center font-bold">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 tracking-tight">Payroll Finalization & Paid Audit Logs</h3>
                  <p className="text-xs text-slate-500">Secure record of administrator actions when marking payroll batches or salary slips as paid</p>
                </div>
              </div>
              <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-xl text-xs font-bold">{auditLogs.length} Events Logged</span>
            </div>

            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-700 uppercase font-bold text-[10px] sticky top-0">
                  <tr>
                    <th className="p-3">Timestamp</th>
                    <th className="p-3">Admin / HR User ID</th>
                    <th className="p-3">Action</th>
                    <th className="p-3">Batch Month</th>
                    <th className="p-3">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-600 font-medium">
                  {auditLogs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-6 text-center text-slate-400">No audit logs recorded yet. Finalize or mark a payroll batch as paid to generate audit events.</td>
                    </tr>
                  ) : (
                    auditLogs.map(log => (
                      <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                        <td className="p-3 font-semibold text-slate-800">
                          {log.timestamp?.toDate ? format(log.timestamp.toDate(), 'yyyy-MM-dd HH:mm:ss') : 'Just now'}
                        </td>
                        <td className="p-3 font-bold text-indigo-700">{log.adminEmail || log.adminUserId}</td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-800 rounded font-bold text-[10px]">
                            {log.action}
                          </span>
                        </td>
                        <td className="p-3 font-bold text-slate-900">{log.batchMonth || '—'}</td>
                        <td className="p-3 text-slate-600">{log.details}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Employee Modal */}
      <AnimatePresence>
        {isEmployeeModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsEmployeeModalOpen(false)} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl p-8 overflow-y-auto max-h-[90vh]">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-slate-900">{editingEmployee ? 'Edit' : 'Add New'} Employee</h2>
                <button onClick={() => { setIsEmployeeModalOpen(false); setEditingEmployee(null); }} className="p-2 hover:bg-slate-100 rounded-xl transition-colors"><X className="w-6 h-6 text-slate-400" /></button>
              </div>
              <form onSubmit={handleEmployeeSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-100">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Employee Code</label>
                    <input type="text" value={employeeForm.employeeCode} onChange={(e) => setEmployeeForm({ ...employeeForm, employeeCode: e.target.value })} className="w-full px-4 py-3 bg-white border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-mono" placeholder="BTS-001" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">First Name *</label>
                    <input required type="text" value={employeeForm.firstName} onChange={(e) => setEmployeeForm({ ...employeeForm, firstName: e.target.value })} className="w-full px-4 py-3 bg-white border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Last Name *</label>
                    <input required type="text" value={employeeForm.lastName} onChange={(e) => setEmployeeForm({ ...employeeForm, lastName: e.target.value })} className="w-full px-4 py-3 bg-white border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Email *</label>
                  <input required type="email" autoComplete="new-password" value={employeeForm.email} onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Phone *</label>
                  <input required type="tel" value={employeeForm.phone} onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Department *</label>
                  <input required type="text" value={employeeForm.department} onChange={(e) => setEmployeeForm({ ...employeeForm, department: e.target.value })} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Position *</label>
                  <input required type="text" value={employeeForm.position} onChange={(e) => setEmployeeForm({ ...employeeForm, position: e.target.value })} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Employee Category *</label>
                  <select 
                    value={employeeForm.category || 'Technician'} 
                    onChange={(e) => setEmployeeForm({ ...employeeForm, category: e.target.value })} 
                    className="w-full px-4 py-3 bg-blue-50 border border-blue-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/20 transition-all font-bold text-blue-900"
                  >
                    {EMPLOYEE_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2 border-t border-slate-100 pt-6">
                  <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest mb-4">Financial & Statutory Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Basic Salary (TZS) *</label>
                      <input required type="number" value={employeeForm.salary || ''} onChange={(e) => setEmployeeForm({ ...employeeForm, salary: Number(e.target.value) })} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">TIN Number</label>
                      <input type="text" value={employeeForm.tin} onChange={(e) => setEmployeeForm({ ...employeeForm, tin: e.target.value })} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all" placeholder="123-456-789" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Joining Date *</label>
                      <input required type="date" value={employeeForm.joiningDate} onChange={(e) => setEmployeeForm({ ...employeeForm, joiningDate: e.target.value })} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">NSSF Number</label>
                      <input type="text" value={employeeForm.nssfNumber} onChange={(e) => setEmployeeForm({ ...employeeForm, nssfNumber: e.target.value })} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">NHIF Number</label>
                      <input type="text" value={employeeForm.nhifNumber} onChange={(e) => setEmployeeForm({ ...employeeForm, nhifNumber: e.target.value })} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all" />
                    </div>
                    <div className="flex items-center gap-3 py-2">
                      <input 
                        type="checkbox" 
                        id="hasHeslbLoan"
                        checked={employeeForm.hasHeslbLoan} 
                        onChange={(e) => setEmployeeForm({ ...employeeForm, hasHeslbLoan: e.target.checked })} 
                        className="w-5 h-5 rounded border-slate-300 text-blue-800 focus:ring-blue-800"
                      />
                      <label htmlFor="hasHeslbLoan" className="text-sm font-semibold text-slate-700 cursor-pointer">Has HESLB Loan (15%)</label>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Tax Residency</label>
                      <select 
                        value={employeeForm.taxResidency || 'resident'} 
                        onChange={(e) => setEmployeeForm({ ...employeeForm, taxResidency: e.target.value as any })} 
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-800"
                      >
                        <option value="resident">Resident</option>
                        <option value="non-resident">Non-Resident (15%)</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-3 py-2">
                      <input 
                        type="checkbox" 
                        id="isTaxExempt"
                        checked={employeeForm.isTaxExempt} 
                        onChange={(e) => setEmployeeForm({ ...employeeForm, isTaxExempt: e.target.checked })} 
                        className="w-5 h-5 rounded border-slate-300 text-blue-800 focus:ring-blue-800"
                      />
                      <label htmlFor="isTaxExempt" className="text-sm font-semibold text-slate-700 cursor-pointer">Tax Exempt (0 PAYE)</label>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Bank Name</label>
                      <input type="text" value={employeeForm.bankName} onChange={(e) => setEmployeeForm({ ...employeeForm, bankName: e.target.value })} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Account Number</label>
                      <input type="text" value={employeeForm.accountNumber} onChange={(e) => setEmployeeForm({ ...employeeForm, accountNumber: e.target.value })} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-mono" />
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2 border-t border-slate-100 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest">Monthly Allowances</h3>
                    <button type="button" onClick={() => setEmployeeForm({ ...employeeForm, allowances: [...employeeForm.allowances, { name: '', amount: 0 }] })} className="text-[10px] font-black text-blue-800 uppercase tracking-widest flex items-center gap-1 hover:text-blue-900 transition-colors bg-blue-50 px-3 py-1 rounded-full"><Plus className="w-3 h-3" /> Add Allowance</button>
                  </div>
                  <div className="space-y-3">
                    {employeeForm.allowances.map((al, idx) => (
                      <div key={idx} className="flex gap-4 items-center animate-in fade-in slide-in-from-top-2">
                        <input type="text" placeholder="Allowance Name (e.g. Transport)" value={al.name} onChange={(e) => {
                          const newAl = [...employeeForm.allowances];
                          newAl[idx].name = e.target.value;
                          setEmployeeForm({ ...employeeForm, allowances: newAl });
                        }} className="flex-1 px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-sm" />
                        <input type="number" placeholder="Amount" value={al.amount || ''} onChange={(e) => {
                          const newAl = [...employeeForm.allowances];
                          newAl[idx].amount = Number(e.target.value);
                          setEmployeeForm({ ...employeeForm, allowances: newAl });
                        }} className="w-32 px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-sm font-bold" />
                        <button type="button" onClick={() => setEmployeeForm({ ...employeeForm, allowances: employeeForm.allowances.filter((_, i) => i !== idx) })} className="p-2 text-slate-300 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
                <button type="submit" disabled={isSubmitting} className="md:col-span-2 py-4 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 flex items-center justify-center gap-2">
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save Employee'}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Deduction & Loan Category Modal */}
      <AnimatePresence>
        {isDeductionModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsDeductionModalOpen(false)} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="relative w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl p-8 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
                <h2 className="text-xl font-bold text-slate-900">{editingDeduction ? 'Edit' : 'Add'} Deduction / Loan Category</h2>
                <button onClick={() => { setIsDeductionModalOpen(false); setEditingDeduction(null); }} className="p-2 hover:bg-slate-100 rounded-xl transition-colors"><X className="w-6 h-6 text-slate-400" /></button>
              </div>
              <form onSubmit={handleDeductionSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Category / Loan Name *</label>
                  <input required type="text" value={deductionForm.name} onChange={(e) => setDeductionForm({ ...deductionForm, name: e.target.value })} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all text-sm font-bold" placeholder="e.g. Staff Salary Loan, NSSF Loan, Union Fees" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">Category Kind</label>
                    <select
                      value={deductionForm.categoryKind || 'loan'}
                      onChange={(e) => setDeductionForm({ ...deductionForm, categoryKind: e.target.value as any })}
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-800"
                    >
                      <option value="loan">Staff Loan</option>
                      <option value="advance">Salary Advance</option>
                      <option value="union">Union Fees</option>
                      <option value="general">General Custom Deduction</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">Calculation Type</label>
                    <select
                      value={deductionForm.type || 'fixed'}
                      onChange={(e) => setDeductionForm({ ...deductionForm, type: e.target.value as any })}
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-800"
                    >
                      <option value="fixed">Flat Rate (TZS)</option>
                      <option value="percentage">Percentage (%)</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700">Monthly Deduction Amount / Percentage *</label>
                  <input
                    type="number"
                    required
                    value={deductionForm.value || 0}
                    onChange={(e) => setDeductionForm({ ...deductionForm, value: Number(e.target.value) })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-sm font-black text-red-600"
                  />
                </div>

                {(deductionForm.categoryKind === 'loan' || deductionForm.categoryKind === 'advance') && (() => {
                  const p = Number(deductionForm.totalPrincipal) || 0;
                  const r = Number(deductionForm.interestRate) || 0;
                  const m = Number(deductionForm.durationMonths) || 12;
                  const totalRet = Math.round(p + (p * (r / 100) * (m / 12)));
                  const monthlyInst = m > 0 ? Math.round(totalRet / m) : 0;
                  return (
                    <div className="bg-red-50/50 p-4 rounded-2xl border border-red-100 space-y-3">
                      <h4 className="text-xs font-black uppercase text-red-700 tracking-wider">Loan & Advance Terms & Calculation</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[11px] font-bold text-slate-600">Date of Taking Loan</label>
                          <input
                            type="date"
                            value={deductionForm.loanDate || new Date().toISOString().split('T')[0]}
                            onChange={(e) => setDeductionForm({ ...deductionForm, loanDate: e.target.value })}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-bold text-slate-600">Duration (Months)</label>
                          <input
                            type="number"
                            min="1"
                            value={deductionForm.durationMonths || 12}
                            onChange={(e) => setDeductionForm({ ...deductionForm, durationMonths: Number(e.target.value) })}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-bold text-slate-600">Interest Rate (%)</label>
                          <input
                            type="number"
                            step="0.1"
                            value={deductionForm.interestRate || 0}
                            onChange={(e) => setDeductionForm({ ...deductionForm, interestRate: Number(e.target.value) })}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-bold text-slate-600">Amount Taken (Principal TZS)</label>
                          <input
                            type="number"
                            value={deductionForm.totalPrincipal || 0}
                            onChange={(e) => setDeductionForm({ ...deductionForm, totalPrincipal: Number(e.target.value) })}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                          />
                        </div>
                      </div>
                      <div className="bg-white p-3 rounded-xl border border-red-200 space-y-1 text-xs">
                        <div className="flex justify-between text-slate-600 font-semibold">
                          <span>Amount to Return (with Interest):</span>
                          <span className="font-bold text-slate-900">TZS {totalRet.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-slate-600 font-semibold">
                          <span>Amount to Pay per Month (Installment):</span>
                          <span className="font-black text-red-600">TZS {monthlyInst.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700">Description / Loan Terms</label>
                  <textarea value={deductionForm.description} onChange={(e) => setDeductionForm({ ...deductionForm, description: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs h-20" placeholder="e.g. 12-month tenure company emergency loan..." />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700">Assign to Employees ({deductionForm.assignedEmployeeIds.length} Selected)</label>
                  <div className="max-h-36 overflow-y-auto border border-slate-100 rounded-xl p-3 bg-slate-50 space-y-2">
                    {employees.map(emp => (
                      <label key={emp.id} className="flex items-center gap-3 cursor-pointer select-none text-xs font-medium">
                        <input
                          type="checkbox"
                          checked={deductionForm.assignedEmployeeIds.includes(emp.id)}
                          onChange={(e) => {
                            const newIds = e.target.checked 
                              ? [...deductionForm.assignedEmployeeIds, emp.id]
                              : deductionForm.assignedEmployeeIds.filter(id => id !== emp.id);
                            setDeductionForm({ ...deductionForm, assignedEmployeeIds: newIds });
                          }}
                          className="w-4 h-4 rounded border-slate-300 text-blue-800 focus:ring-blue-800 cursor-pointer"
                        />
                        <span>{emp.firstName} {emp.lastName} ({emp.employeeCode || emp.department})</span>
                      </label>
                    ))}
                  </div>
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full py-3.5 bg-blue-800 text-white rounded-xl font-bold text-sm hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 flex items-center justify-center gap-2">
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingDeduction ? 'Update Deduction Category' : 'Save Deduction Category')}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Export Options Modal */}
      <AnimatePresence>
        {isExportModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsExportModalOpen(false)} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="relative w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl p-8">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <FileSpreadsheet className="w-6 h-6 text-emerald-700" /> Excel / CSV Export Configuration
                  </h2>
                  <p className="text-xs text-slate-500 font-medium">Select specific statutory and custom deduction columns to include in the exported report</p>
                </div>
                <button onClick={() => setIsExportModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors"><X className="w-6 h-6 text-slate-400" /></button>
              </div>

              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-3">
                  <h4 className="text-xs font-black uppercase text-slate-500 tracking-wider">Statutory Deductions & Contributions</h4>
                  
                  <div className="grid grid-cols-2 gap-3 text-xs font-semibold text-slate-700">
                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={exportOpts.includeNSSFEmployee}
                        onChange={(e) => setExportOpts({ ...exportOpts, includeNSSFEmployee: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                      />
                      <span>NSSF Pension (10%)</span>
                    </label>

                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={exportOpts.includePAYE}
                        onChange={(e) => setExportOpts({ ...exportOpts, includePAYE: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                      />
                      <span>PAYE Income Tax</span>
                    </label>

                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={exportOpts.includeNHIFEmployee}
                        onChange={(e) => setExportOpts({ ...exportOpts, includeNHIFEmployee: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                      />
                      <span>NHIF Health Insurance</span>
                    </label>

                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={exportOpts.includeHESLB}
                        onChange={(e) => setExportOpts({ ...exportOpts, includeHESLB: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                      />
                      <span>HESLB Student Loans</span>
                    </label>

                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={exportOpts.includeWCF}
                        onChange={(e) => setExportOpts({ ...exportOpts, includeWCF: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                      />
                      <span>WCF Contribution</span>
                    </label>

                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={exportOpts.includeSDL}
                        onChange={(e) => setExportOpts({ ...exportOpts, includeSDL: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                      />
                      <span>SDL Skills Levy</span>
                    </label>
                  </div>
                </div>

                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-3">
                  <h4 className="text-xs font-black uppercase text-slate-500 tracking-wider">Custom Employee Loans & Others</h4>
                  <label className="flex items-center gap-2.5 cursor-pointer select-none text-xs font-semibold text-slate-700">
                    <input
                      type="checkbox"
                      checked={exportOpts.includeLoansAndOtherDeductions}
                      onChange={(e) => setExportOpts({ ...exportOpts, includeLoansAndOtherDeductions: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                    />
                    <span>Include Custom Loan Deductions & Advances</span>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => setIsExportModalOpen(false)}
                    className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const recordsToExport = selectedBatchMonths.length > 0
                        ? filteredPayrolls.filter(p => selectedBatchMonths.includes(p.month))
                        : filteredPayrolls;
                      const periodLabel = selectedBatchMonths.length > 0 ? selectedBatchMonths.join('_') : 'Active_Period';
                      exportPayrollCSV(recordsToExport, periodLabel);
                      setIsExportModalOpen(false);
                      toast.success('Customized Excel/CSV Payroll Report downloaded');
                    }}
                    className="px-6 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs rounded-xl transition-all shadow-lg shadow-emerald-700/20 flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" /> Download Excel/CSV
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Compliance Claim Modal */}
      <AnimatePresence>
        {isComplianceModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsComplianceModalOpen(false)} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="relative w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl p-8 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <ShieldCheck className="w-6 h-6 text-emerald-600" /> Launch Statutory Claim / Compliance Record
                  </h2>
                  <p className="text-xs text-slate-500 font-medium">Record WCF injury claims, OSHA health inspection status, or NHIF card updates</p>
                </div>
                <button onClick={() => setIsComplianceModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors"><X className="w-6 h-6 text-slate-400" /></button>
              </div>

              <form onSubmit={handleComplianceSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700">Select Employee *</label>
                  <select
                    required
                    value={complianceForm.employeeId}
                    onChange={(e) => setComplianceForm({ ...complianceForm, employeeId: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-800"
                  >
                    <option value="">-- Choose Staff Member --</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.firstName} {emp.lastName} ({emp.employeeCode || emp.department})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">Statutory Authority *</label>
                    <select
                      value={complianceForm.authority}
                      onChange={(e) => setComplianceForm({ ...complianceForm, authority: e.target.value as any })}
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-800"
                    >
                      <option value="WCF">WCF (Workers Comp)</option>
                      <option value="OSHA">OSHA (Safety & Med Exam)</option>
                      <option value="NHIF">NHIF (Health Insurance)</option>
                      <option value="NSSF">NSSF (Pension Fund)</option>
                      <option value="SDL">SDL (Skills Levy)</option>
                      <option value="HESLB">HESLB (Student Loans)</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">Filing Status</label>
                    <select
                      value={complianceForm.status}
                      onChange={(e) => setComplianceForm({ ...complianceForm, status: e.target.value as any })}
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-800"
                    >
                      <option value="submitted">Submitted</option>
                      <option value="under_review">Under Review</option>
                      <option value="approved">Approved</option>
                      <option value="paid">Paid Out</option>
                      <option value="compliant">Compliant</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700">Claim / Filing Title *</label>
                  <input
                    type="text"
                    required
                    value={complianceForm.title}
                    onChange={(e) => setComplianceForm({ ...complianceForm, title: e.target.value })}
                    placeholder="e.g. Workplace Minor Injury WCF Claim, OSHA Medical Certificate Renewal"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">Reference Number</label>
                    <input
                      type="text"
                      value={complianceForm.referenceNumber}
                      onChange={(e) => setComplianceForm({ ...complianceForm, referenceNumber: e.target.value })}
                      placeholder="e.g. WCF-2026-991"
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-mono"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">Claim Amount (TZS)</label>
                    <input
                      type="number"
                      value={complianceForm.claimAmount || 0}
                      onChange={(e) => setComplianceForm({ ...complianceForm, claimAmount: Number(e.target.value) })}
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-emerald-600"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">Incident / Filing Date</label>
                    <input
                      type="date"
                      value={complianceForm.incidentOrEventDate}
                      onChange={(e) => setComplianceForm({ ...complianceForm, incidentOrEventDate: e.target.value })}
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">Expiry / Due Date</label>
                    <input
                      type="date"
                      value={complianceForm.expiryOrDueDate}
                      onChange={(e) => setComplianceForm({ ...complianceForm, expiryOrDueDate: e.target.value })}
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700">Notes & Filing Details</label>
                  <textarea
                    value={complianceForm.notes}
                    onChange={(e) => setComplianceForm({ ...complianceForm, notes: e.target.value })}
                    placeholder="Attach details of incident or OSHA medical inspection notes..."
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs h-20"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl font-bold text-xs transition-all shadow-lg shadow-emerald-700/20 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Launch Statutory Record / Claim'}
                </button>
              </form>
            </motion.div>
          </div>
        )}

        {/* Bulk Compliance CSV Upload Modal */}
        {isBulkComplianceModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !isBulkUploading && setIsBulkComplianceModalOpen(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="relative w-full max-w-4xl bg-white rounded-[2.5rem] shadow-2xl p-6 md:p-8 max-h-[90vh] flex flex-col overflow-hidden">
              
              {/* Modal Header */}
              <div className="flex items-center justify-between pb-4 border-b border-slate-100 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-50 text-emerald-700 rounded-2xl flex items-center justify-center font-bold">
                    <FileSpreadsheet className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 tracking-tight">Bulk Statutory Compliance & Documents Upload</h2>
                    <p className="text-xs text-slate-500 font-medium">Map CSV files containing multiple employees' compliance updates & claims directly into the register.</p>
                  </div>
                </div>
                <button disabled={isBulkUploading} onClick={() => setIsBulkComplianceModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Scrollable Content Body */}
              <div className="flex-1 overflow-y-auto py-6 space-y-6">

                {/* Upload & Instructions Card */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2 border-2 border-dashed border-emerald-200 bg-emerald-50/30 hover:bg-emerald-50/60 rounded-3xl p-6 text-center transition-all relative flex flex-col items-center justify-center">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleComplianceCsvFileUpload}
                      disabled={isBulkUploading}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                    <UploadCloud className="w-10 h-10 text-emerald-600 mb-2" />
                    <p className="text-sm font-bold text-slate-900">
                      {bulkComplianceFileName ? `Selected: ${bulkComplianceFileName}` : 'Click or Drag CSV file here to upload'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Supports UTF-8 CSV formatted employee compliance records</p>
                  </div>

                  <div className="bg-slate-50 rounded-3xl p-5 border border-slate-100 flex flex-col justify-between space-y-3">
                    <div>
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                        <Download className="w-4 h-4 text-emerald-600" /> Standard Template
                      </h4>
                      <p className="text-[11px] text-slate-500">Download formatted template containing standard columns: NSSF, NHIF, WCF, OSHA, HESLB & Claims.</p>
                    </div>
                    <button
                      type="button"
                      onClick={downloadComplianceTemplate}
                      className="w-full py-2.5 bg-white hover:bg-slate-100 text-slate-800 border border-slate-200 font-bold rounded-xl text-xs transition-colors flex items-center justify-center gap-2 shadow-sm"
                    >
                      <Download className="w-4 h-4 text-emerald-600" />
                      Download Sample CSV
                    </button>
                  </div>
                </div>

                {/* Result Summary Banner (if finished) */}
                {bulkUploadSummary && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-between text-xs">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                      <div>
                        <p className="font-bold text-emerald-900">Bulk Upload Completed Successfully</p>
                        <p className="text-emerald-700">
                          Updated <span className="font-black">{bulkUploadSummary.updatedProfiles}</span> employee statutory profiles and created <span className="font-black">{bulkUploadSummary.createdClaims}</span> compliance claims.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setBulkUploadSummary(null)}
                      className="text-emerald-800 font-bold underline hover:text-emerald-900"
                    >
                      Dismiss
                    </button>
                  </div>
                )}

                {/* Parsed Rows KPIs & Table */}
                {bulkComplianceRows.length > 0 && (
                  <div className="space-y-4">
                    {/* KPI header */}
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                      <div className="flex items-center gap-4 text-xs">
                        <div>
                          <span className="text-slate-400 font-bold uppercase text-[10px]">Total Parsed</span>
                          <p className="font-black text-slate-900 text-base">{bulkComplianceRows.length} Rows</p>
                        </div>
                        <div className="h-6 w-px bg-slate-200" />
                        <div>
                          <span className="text-emerald-600 font-bold uppercase text-[10px]">Matched</span>
                          <p className="font-black text-emerald-700 text-base">
                            {bulkComplianceRows.filter(r => r.matchedEmployee).length} Staff
                          </p>
                        </div>
                        <div className="h-6 w-px bg-slate-200" />
                        <div>
                          <span className="text-rose-500 font-bold uppercase text-[10px]">Unmatched</span>
                          <p className="font-black text-rose-600 text-base">
                            {bulkComplianceRows.filter(r => !r.matchedEmployee).length} Skipped
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const allSelected = bulkComplianceRows.every(r => r.selected);
                            setBulkComplianceRows(
                              bulkComplianceRows.map(r => ({ ...r, selected: !allSelected && !!r.matchedEmployee }))
                            );
                          }}
                          className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-xl transition-colors"
                        >
                          Toggle Selection
                        </button>
                      </div>
                    </div>

                    {/* Preview Table */}
                    <div className="border border-slate-200 rounded-2xl overflow-hidden max-h-[300px] overflow-y-auto">
                      <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-100 text-[10px] uppercase font-bold text-slate-600 sticky top-0 border-b border-slate-200">
                          <tr>
                            <th className="p-3 w-10 text-center">
                              <input
                                type="checkbox"
                                checked={bulkComplianceRows.length > 0 && bulkComplianceRows.every(r => r.selected)}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setBulkComplianceRows(
                                    bulkComplianceRows.map(r => ({ ...r, selected: checked && !!r.matchedEmployee }))
                                  );
                                }}
                                className="rounded text-emerald-600 focus:ring-emerald-500"
                              />
                            </th>
                            <th className="p-3">CSV Identifier</th>
                            <th className="p-3">Matched Employee</th>
                            <th className="p-3">Statutory Profile Updates</th>
                            <th className="p-3">Compliance Claim / Log</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-xs font-medium">
                          {bulkComplianceRows.map(row => (
                            <tr key={row.rowIndex} className={cn("hover:bg-slate-50/80 transition-colors", !row.matchedEmployee && "bg-rose-50/30")}>
                              <td className="p-3 text-center">
                                <input
                                  type="checkbox"
                                  disabled={!row.matchedEmployee || isBulkUploading}
                                  checked={row.selected}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setBulkComplianceRows(
                                      bulkComplianceRows.map(r => r.rowIndex === row.rowIndex ? { ...r, selected: checked } : r)
                                    );
                                  }}
                                  className="rounded text-emerald-600 focus:ring-emerald-500 disabled:opacity-30 cursor-pointer"
                                />
                              </td>
                              <td className="p-3">
                                <p className="font-bold text-slate-900 font-mono">{row.rawIdentifier}</p>
                                {row.employeeNameInput && row.employeeNameInput !== row.rawIdentifier && (
                                  <p className="text-[10px] text-slate-500">{row.employeeNameInput}</p>
                                )}
                              </td>
                              <td className="p-3">
                                {row.matchedEmployee ? (
                                  <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 bg-emerald-100 text-emerald-800 font-bold rounded-lg flex items-center justify-center text-[10px] shrink-0">
                                      {row.matchedEmployee.firstName[0]}{row.matchedEmployee.lastName[0]}
                                    </div>
                                    <div>
                                      <p className="font-bold text-slate-900">{row.matchedEmployee.firstName} {row.matchedEmployee.lastName}</p>
                                      <p className="text-[10px] text-slate-500 font-mono">{row.matchedEmployee.employeeCode || row.matchedEmployee.department}</p>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="px-2 py-0.5 bg-rose-100 text-rose-700 font-bold rounded text-[10px] flex items-center gap-1 w-fit">
                                    <AlertCircle className="w-3 h-3" /> Unmatched
                                  </span>
                                )}
                              </td>
                              <td className="p-3 text-[11px] space-y-0.5">
                                {row.nssfNumber && <p className="text-slate-700">NSSF: <span className="font-mono font-bold text-emerald-700">{row.nssfNumber}</span></p>}
                                {row.nhifNumber && <p className="text-slate-700">NHIF: <span className="font-mono font-bold text-emerald-700">{row.nhifNumber}</span></p>}
                                {row.wcfNumber && <p className="text-slate-700">WCF: <span className="font-mono font-bold text-emerald-700">{row.wcfNumber}</span></p>}
                                {row.oshaStatus && <p className="text-slate-700">OSHA Status: <span className="font-bold uppercase text-blue-700">{row.oshaStatus}</span></p>}
                                {row.heslbBalance !== undefined && <p className="text-slate-700">HESLB Bal: <span className="font-bold text-rose-600">TZS {row.heslbBalance.toLocaleString()}</span></p>}
                                {!row.nssfNumber && !row.nhifNumber && !row.wcfNumber && !row.oshaStatus && row.heslbBalance === undefined && (
                                  <span className="text-slate-400 italic">No direct profile updates</span>
                                )}
                              </td>
                              <td className="p-3 text-[11px]">
                                {row.claimTitle ? (
                                  <div className="space-y-0.5">
                                    <span className="px-2 py-0.5 bg-blue-50 text-blue-800 font-bold rounded text-[10px]">
                                      {row.claimAuthority || 'Claim'}: {row.claimTitle}
                                    </span>
                                    {row.claimAmount ? (
                                      <p className="font-bold text-slate-800">TZS {row.claimAmount.toLocaleString()}</p>
                                    ) : null}
                                  </div>
                                ) : (
                                  <span className="text-slate-400 italic">No claim log attached</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer Controls */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-between shrink-0">
                <p className="text-xs text-slate-500 font-medium">
                  Selected <span className="font-bold text-emerald-700">{bulkComplianceRows.filter(r => r.selected && r.matchedEmployee).length}</span> item(s) ready for bulk execution.
                </p>

                <div className="flex items-center gap-3">
                  <button
                    disabled={isBulkUploading}
                    onClick={() => setIsBulkComplianceModalOpen(false)}
                    className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors"
                  >
                    Close
                  </button>
                  <button
                    disabled={isBulkUploading || bulkComplianceRows.filter(r => r.selected && r.matchedEmployee).length === 0}
                    onClick={processBulkComplianceUpload}
                    className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs transition-all shadow-md disabled:opacity-50 flex items-center gap-2"
                  >
                    {isBulkUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    <span>Process Selected Bulk Upload</span>
                  </button>
                </div>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Payroll Details Modal */}
      <AnimatePresence>
        {isPayrollDetailsModalOpen && selectedMonth && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsPayrollDetailsModalOpen(false)} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="relative w-full max-w-7xl bg-white rounded-[2.5rem] shadow-2xl p-8 overflow-y-auto max-h-[95vh]">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                <div>
                  <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                    <FileText className="w-6 h-6 text-teal-700" /> Employee Payroll Detail (TZS) - {selectedMonth}
                  </h2>
                  <p className="text-sm text-slate-500 font-medium">Mark records and hit the Paid button to send salary slips to employee dashboards.</p>
                </div>
                <button onClick={() => setIsPayrollDetailsModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors"><X className="w-6 h-6 text-slate-400" /></button>
              </div>

              {/* Summary Cards matching Payroll 2 image */}
              {(() => {
                const monthPayrolls = filteredPayrolls.filter(p => p.month === selectedMonth);
                const totalEmp = monthPayrolls.length;
                const totalGross = monthPayrolls.reduce((sum, p) => sum + p.grossPay, 0);
                const totalDed = monthPayrolls.reduce((sum, p) => sum + p.totalDeductions, 0);
                const totalNet = monthPayrolls.reduce((sum, p) => sum + p.netSalary, 0);
                const totalEmplrContrib = monthPayrolls.reduce((sum, p) => sum + (p.nssfEmployer + p.sdl + p.wcf + p.nhifEmployer), 0);
                const totalCompanyCost = totalGross + totalEmplrContrib;

                return (
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
                    <div className="bg-slate-800 text-white p-4 rounded-2xl text-center shadow-sm">
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-300 mb-1">Employees</div>
                      <div className="text-xl font-black">{totalEmp}</div>
                    </div>
                    <div className="bg-teal-800 text-white p-4 rounded-2xl text-center shadow-sm">
                      <div className="text-[10px] font-black uppercase tracking-widest text-teal-200 mb-1">Total Gross</div>
                      <div className="text-sm font-black">{totalGross.toLocaleString()}</div>
                    </div>
                    <div className="bg-amber-600 text-white p-4 rounded-2xl text-center shadow-sm">
                      <div className="text-[10px] font-black uppercase tracking-widest text-amber-100 mb-1">Total Deductions</div>
                      <div className="text-sm font-black">{totalDed.toLocaleString()}</div>
                    </div>
                    <div className="bg-emerald-600 text-white p-4 rounded-2xl text-center shadow-sm">
                      <div className="text-[10px] font-black uppercase tracking-widest text-emerald-100 mb-1">Total Net Pay</div>
                      <div className="text-sm font-black">{totalNet.toLocaleString()}</div>
                    </div>
                    <div className="bg-indigo-700 text-white p-4 rounded-2xl text-center shadow-sm">
                      <div className="text-[10px] font-black uppercase tracking-widest text-indigo-200 mb-1">Employer Contrib.</div>
                      <div className="text-sm font-black">{totalEmplrContrib.toLocaleString()}</div>
                    </div>
                    <div className="bg-rose-700 text-white p-4 rounded-2xl text-center shadow-sm">
                      <div className="text-[10px] font-black uppercase tracking-widest text-rose-200 mb-1">Total Company Cost</div>
                      <div className="text-sm font-black">{totalCompanyCost.toLocaleString()}</div>
                    </div>
                  </div>
                );
              })()}

              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 rounded-xl text-xs font-medium flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>SDL not applied — fewer than or equal to 10 employees. SDL (3.5%) only applies when employer has more than 10 employees.</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  <button
                    onClick={() => {
                      const monthPayrolls = filteredPayrolls.filter(p => p.month === selectedMonth);
                      exportPayrollCSV(monthPayrolls, selectedMonth || 'Period');
                    }}
                    className="px-4 py-2.5 bg-blue-800 hover:bg-blue-900 text-white rounded-xl font-bold text-xs shadow-md shadow-blue-800/20 flex items-center gap-2 transition-all"
                  >
                    <Download className="w-4 h-4" /> Download CSV
                  </button>

                  {selectedPayrollIds.length > 0 && canManageHR && (
                    <button
                      onClick={() => {
                        const monthPayrolls = filteredPayrolls.filter(p => p.month === selectedMonth && selectedPayrollIds.includes(p.id) && p.status !== 'paid');
                        setConfirmModal({
                          isOpen: true,
                          title: 'Mark Selected Employees as Paid',
                          message: `Are you sure you want to mark ${monthPayrolls.length} selected employee salary record(s) for ${selectedMonth} as Paid and dispatch salary slips?`,
                          onConfirm: async () => {
                            setConfirmModal(prev => ({ ...prev, isOpen: false }));
                            const batch = writeBatch(db);
                            const emailPromises = [];
                            for (const p of monthPayrolls) {
                              batch.update(doc(db, 'payrolls', p.id), { status: 'paid', updatedAt: Timestamp.now() });
                              emailPromises.push(sendSalarySlipEmail(p));
                            }
                            await batch.commit();
                            Promise.all(emailPromises).catch(console.error);

                            await logAudit('MARK_SELECTED_PAID', `Marked ${monthPayrolls.length} selected employee salary records for ${selectedMonth} as Paid`, selectedMonth || 'General');
                            setSelectedPayrollIds([]);
                            toast.success(`Marked ${monthPayrolls.length} selected salary slip(s) as Paid and dispatched!`);
                          }
                        });
                      }}
                      className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs shadow-lg shadow-emerald-600/20 flex items-center gap-2 transition-all"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Mark Selected ({selectedPayrollIds.length}) as Paid
                    </button>
                  )}

                  <button
                    onClick={() => {
                      const monthPayrolls = filteredPayrolls.filter(p => p.month === selectedMonth);
                      setConfirmModal({
                        isOpen: true,
                        title: 'Mark All Batch Records as Paid',
                        message: `Are you sure you want to finalize and mark all ${monthPayrolls.length} employee salary records for ${selectedMonth} as Paid and dispatch salary slips?`,
                        onConfirm: async () => {
                          setConfirmModal(prev => ({ ...prev, isOpen: false }));
                          const batch = writeBatch(db);
                          const emailPromises = [];
                          for (const p of monthPayrolls) {
                            batch.update(doc(db, 'payrolls', p.id), { status: 'paid', updatedAt: Timestamp.now() });
                            emailPromises.push(sendSalarySlipEmail(p));
                          }
                          await batch.commit();
                          Promise.all(emailPromises).catch(console.error);
                          
                          await logAudit('MARK_BATCH_PAID', `Finalized and marked entire payroll batch for ${selectedMonth} (${monthPayrolls.length} employees) as Paid`, selectedMonth || 'General');
                          toast.success('All salary slips marked as Paid and dispatched to employee dashboards!');
                        }
                      });
                    }}
                    className="px-5 py-2.5 bg-teal-800 hover:bg-teal-900 text-white rounded-xl font-bold text-xs shadow-lg shadow-teal-800/20 flex items-center gap-2 transition-all"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Mark All as Paid & Dispatch
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
                <div className="overflow-x-auto max-h-[450px]">
                  <table className="w-full text-left text-xs whitespace-nowrap">
                    <thead className="bg-slate-900 text-white uppercase text-[10px] font-bold sticky top-0 z-10">
                      <tr>
                        <th className="p-3">
                          <input
                            type="checkbox"
                            checked={(() => {
                              const monthPayrolls = filteredPayrolls.filter(p => p.month === selectedMonth);
                              return monthPayrolls.length > 0 && monthPayrolls.every(p => selectedPayrollIds.includes(p.id) || p.status === 'paid');
                            })()}
                            onChange={(e) => {
                              const monthPayrolls = filteredPayrolls.filter(p => p.month === selectedMonth);
                              if (e.target.checked) {
                                setSelectedPayrollIds(monthPayrolls.map(p => p.id));
                              } else {
                                setSelectedPayrollIds([]);
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-700 text-teal-500 focus:ring-teal-500 cursor-pointer"
                            title="Select all pending employees in this period"
                          />
                        </th>
                        <th className="p-3">Employee</th>
                        <th className="p-3">Type</th>
                        <th className="p-3">Nationality</th>
                        <th className="p-3">Sector</th>
                        <th className="p-3 text-right">Basic</th>
                        <th className="p-3 text-right">Allowances</th>
                        <th className="p-3 text-right">Gross</th>
                        <th className="p-3 text-right">PAYE / Dir.Tax</th>
                        <th className="p-3 text-right">NSSF/PSSF</th>
                        <th className="p-3 text-right">NHIF</th>
                        <th className="p-3 text-right">HESLB</th>
                        <th className="p-3 text-right">Total Ded.</th>
                        <th className="p-3 text-right">Net Pay</th>
                        <th className="p-3 text-right">Emplr Contrib.</th>
                        <th className="p-3 text-right">Company Cost</th>
                        <th className="p-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                      {filteredPayrolls.filter(p => p.month === selectedMonth).map((p, index) => {
                        const totalEmplr = p.nssfEmployer + p.sdl + p.wcf + p.nhifEmployer;
                        const companyCost = p.grossPay + totalEmplr;
                        const isPaid = p.status === 'paid';
                        const isSelected = selectedPayrollIds.includes(p.id);

                        return (
                          <motion.tr
                            key={p.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            whileHover={{ backgroundColor: '#f8fafc' }}
                            transition={{ duration: 0.15, delay: index * 0.02 }}
                            className={cn(
                              "transition-colors",
                              isSelected ? "bg-teal-50/50" : ""
                            )}
                          >
                            <td className="p-3">
                              <input
                                type="checkbox"
                                checked={isSelected || isPaid}
                                disabled={isPaid}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedPayrollIds(prev => [...prev, p.id]);
                                  } else {
                                    setSelectedPayrollIds(prev => prev.filter(id => id !== p.id));
                                  }
                                }}
                                className="w-4 h-4 rounded border-slate-300 text-teal-700 focus:ring-teal-700 cursor-pointer disabled:opacity-50"
                                title={isPaid ? "Already Paid" : "Select employee record for bulk action"}
                              />
                            </td>
                            <td className="p-3 font-bold text-slate-900">{p.employeeName}</td>
                            <td className="p-3"><span className="px-2 py-0.5 bg-teal-50 text-teal-800 rounded font-bold text-[10px]">Primary</span></td>
                            <td className="p-3"><span className="px-2 py-0.5 bg-emerald-50 text-emerald-800 rounded font-bold text-[10px]">Resident</span></td>
                            <td className="p-3"><span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded font-bold text-[10px]">Private</span></td>
                            <td className="p-3 text-right font-bold">TZS {p.basicSalary.toLocaleString()}</td>
                            <td className="p-3 text-right">TZS {(p.grossPay - p.basicSalary).toLocaleString()}</td>
                            <td className="p-3 text-right font-black text-slate-900">TZS {p.grossPay.toLocaleString()}</td>
                            <td className="p-3 text-right text-red-600">{p.paye.toLocaleString()}</td>
                            <td className="p-3 text-right text-red-600">{p.nssfEmployee.toLocaleString()}</td>
                            <td className="p-3 text-right text-red-600">{p.nhifEmployee.toLocaleString()}</td>
                            <td className="p-3 text-right text-red-600">{p.heslb ? p.heslb.toLocaleString() : '—'}</td>
                            <td className="p-3 text-right font-bold text-red-700">TZS {p.totalDeductions.toLocaleString()}</td>
                            <td className="p-3 text-right font-black text-emerald-600">TZS {p.netSalary.toLocaleString()}</td>
                            <td className="p-3 text-right text-indigo-700">TZS {totalEmplr.toLocaleString()}</td>
                            <td className="p-3 text-right font-bold text-rose-700">TZS {companyCost.toLocaleString()}</td>
                            <td className="p-3 text-center">
                              <div className="flex items-center justify-center gap-1.5">
                                <button
                                  onClick={() => generateSalarySlip(p, 'download')}
                                  className="px-2.5 py-1 bg-red-600 text-white rounded font-bold text-[10px] hover:bg-red-700 transition-all flex items-center gap-1 shadow-sm"
                                  title="Download Slip PDF"
                                >
                                  <FileText className="w-3 h-3" /> Slip PDF
                                </button>
                                <button
                                  onClick={() => generateSalarySlip(p, 'print')}
                                  className="px-2.5 py-1 bg-teal-700 text-white rounded font-bold text-[10px] hover:bg-teal-800 transition-all flex items-center gap-1 shadow-sm"
                                  title="Download Slip XLS / Print"
                                >
                                  <Download className="w-3 h-3" /> Slip XLS
                                </button>
                                {isPaid ? (
                                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold text-[10px]">Paid & Sent</span>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setConfirmModal({
                                        isOpen: true,
                                        title: 'Mark Salary as Paid',
                                        message: `Are you sure you want to mark the salary slip for ${p.employeeName} (${p.month}) as Paid and dispatch it to their dashboard?`,
                                        onConfirm: async () => {
                                          await updateDoc(doc(db, 'payrolls', p.id), { status: 'paid', updatedAt: Timestamp.now() });
                                          await sendSalarySlipEmail(p);
                                          await logAudit('MARK_INDIVIDUAL_PAID', `Marked salary slip for ${p.employeeName} (${p.month}) as Paid`, p.month);
                                          toast.success(`Salary marked as paid and sent to ${p.employeeName}!`);
                                        }
                                      });
                                    }}
                                    className="px-2 py-0.5 bg-slate-200 text-slate-800 rounded font-bold text-[10px] hover:bg-emerald-600 hover:text-white transition-all"
                                  >
                                    Mark Paid
                                  </button>
                                )}
                              </div>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {activeTab === 'attendance' && (
        <div className="space-y-8">
          {/* Main Attendance Logging Container */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Column 1: Terminal Clock-in/out */}
            <div className="lg:col-span-1 bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm flex flex-col justify-between min-h-[500px]">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-800">
                    <Clock className="w-6 h-6 animate-pulse" />
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-slate-900 tracking-tight">Time Clock</h2>
                    <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Automated Terminal</p>
                  </div>
                </div>

                {/* Digital Clock display */}
                <div className="bg-slate-50/50 rounded-2xl p-6 border border-slate-100 text-center mb-6">
                  <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-1">
                    {format(currentTime, 'EEEE, d MMMM yyyy')}
                  </p>
                  <p className="text-4xl font-black text-slate-900 tracking-tight font-mono">
                    {format(currentTime, 'hh:mm:ss a')}
                  </p>
                </div>

                {/* Selected Employee Display */}
                <div className="space-y-4 mb-6">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Employee Profile</label>
                    {isAdmin || isHR || isHOD ? (
                      <select 
                        value={attendanceEmployeeId} 
                        onChange={(e) => {
                          setAttendanceEmployeeId(e.target.value);
                          setGeoError(null);
                          setGeoCoords(null);
                        }} 
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                      >
                        <option value="">-- Select Employee --</option>
                        {employees.map(emp => (
                          <option key={emp.id} value={emp.id}>
                            {emp.firstName} {emp.lastName} ({emp.employeeCode || emp.id.substring(0, 5)})
                          </option>
                        ))}
                      </select>
                    ) : (
                      (() => {
                        const emp = employees.find(e => e.id === attendanceEmployeeId);
                        return emp ? (
                          <div className="bg-blue-50/30 border border-blue-50 p-4 rounded-2xl flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center text-blue-800 font-bold">
                              {emp.firstName[0]}{emp.lastName[0]}
                            </div>
                            <div>
                              <p className="font-bold text-slate-900 text-sm">{emp.firstName} {emp.lastName}</p>
                              <p className="text-xs text-slate-500 font-medium">{emp.position} • {emp.department}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-amber-50/30 border border-amber-100 p-4 rounded-2xl text-xs text-amber-700 font-medium">
                            No matching employee record found for your account email ({profile?.email}). Please contact HR to set up your profile.
                          </div>
                        );
                      })()
                    )}
                  </div>

                  {/* Note textarea */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Notes / Comment (Optional)</label>
                    <textarea 
                      value={attendanceNote} 
                      onChange={(e) => setAttendanceNote(e.target.value)} 
                      placeholder="e.g. Field visit, Client site installation, Work from Home..."
                      rows={2}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-4">
                {(() => {
                  const emp = employees.find(e => e.id === attendanceEmployeeId);
                  if (!emp) return (
                    <div className="text-center p-4 bg-slate-50 text-xs text-slate-400 font-medium rounded-2xl border border-slate-100">
                      Select an employee to proceed with check-in/out
                    </div>
                  );
                  const lastLog = getEmployeeLastStatus(emp.id);
                  const isCheckedIn = lastLog && lastLog.type === 'check-in';

                  return (
                    <div className="space-y-4">
                      {isCheckedIn ? (
                        <button 
                          onClick={() => performCheckInOut('check-out', emp, attendanceNote)}
                          disabled={geoLoading}
                          className="w-full py-4 bg-orange-600 hover:bg-orange-700 text-white rounded-2xl font-bold transition-all shadow-lg shadow-orange-100 flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                        >
                          {geoLoading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <LogOut className="w-5 h-5" />
                          )}
                          CHECK OUT
                        </button>
                      ) : (
                        <button 
                          onClick={() => performCheckInOut('check-in', emp, attendanceNote)}
                          disabled={geoLoading}
                          className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-bold transition-all shadow-lg shadow-emerald-100 flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                        >
                          {geoLoading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <LogIn className="w-5 h-5" />
                          )}
                          CHECK IN
                        </button>
                      )}

                      {/* GPS Indicator Status */}
                      <div className="flex items-center justify-center gap-2 text-xs font-bold text-slate-400 bg-slate-50 p-3 rounded-xl border border-slate-100">
                        {geoLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                            <span>Acquiring GPS coordinates...</span>
                          </>
                        ) : geoError ? (
                          <>
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                            <span className="text-amber-600">Location error: {geoError}. Saved timestamp only.</span>
                          </>
                        ) : geoCoords ? (
                          <>
                            <MapPin className="w-4 h-4 text-emerald-600" />
                            <span className="text-emerald-700">GPS Captured: {geoCoords.latitude.toFixed(4)}, {geoCoords.longitude.toFixed(4)}</span>
                          </>
                        ) : (
                          <>
                            <Navigation className="w-4 h-4 text-slate-400 animate-pulse" />
                            <span>GPS ready. Location will be logged automatically.</span>
                          </>
                        )}
                      </div>

                      {/* Display last logged action */}
                      {lastLog && (
                        <div className="text-center text-[11px] font-medium text-slate-400 italic mt-2">
                          Last active: Checked {lastLog.type === 'check-in' ? 'in' : 'out'} on {format(lastLog.timestamp.toDate(), 'MMM d, h:mm a')}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Column 2: Stats & Active Onsite List */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Quick stats board */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                  <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider mb-1">Total Checked-In Today</p>
                  <p className="text-2xl font-black text-emerald-600">
                    {attendanceLogs.filter(log => log.type === 'check-in' && format(log.timestamp.toDate(), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')).length}
                  </p>
                </div>
                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                  <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider mb-1">Total Checked-Out Today</p>
                  <p className="text-2xl font-black text-orange-600">
                    {attendanceLogs.filter(log => log.type === 'check-out' && format(log.timestamp.toDate(), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')).length}
                  </p>
                </div>
                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm col-span-2 md:col-span-1">
                  <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider mb-1">Currently Onsite</p>
                  <p className="text-2xl font-black text-blue-800">
                    {employees.filter(e => {
                      const status = getEmployeeLastStatus(e.id);
                      return status && status.type === 'check-in';
                    }).length}
                  </p>
                </div>
              </div>

              {/* List of currently active onsite employees */}
              <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                    Active Onsite Employees
                  </h3>
                  <span className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full font-bold">
                    {employees.filter(e => {
                      const status = getEmployeeLastStatus(e.id);
                      return status && status.type === 'check-in';
                    }).length} Active
                  </span>
                </div>

                <div className="max-h-56 overflow-y-auto space-y-3 pr-2 scrollbar-thin">
                  {(() => {
                    const activeOnsite = employees.filter(e => {
                      const status = getEmployeeLastStatus(e.id);
                      return status && status.type === 'check-in';
                    });

                    if (activeOnsite.length === 0) {
                      return (
                        <div className="text-center py-8 text-sm text-slate-400 font-medium">
                          No employees are currently checked in.
                        </div>
                      );
                    }

                    return activeOnsite.map(emp => {
                      const lastStatus = getEmployeeLastStatus(emp.id);
                      return (
                        <div key={emp.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-2xl border border-slate-100/50 hover:bg-slate-100/40 transition-all">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center text-emerald-600 text-xs font-bold">
                              {emp.firstName[0]}{emp.lastName[0]}
                            </div>
                            <div>
                              <p className="text-xs font-bold text-slate-900">{emp.firstName} {emp.lastName}</p>
                              <p className="text-[10px] text-slate-500">{emp.position} • {emp.department}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-black uppercase">
                              IN
                            </span>
                            <p className="text-[10px] text-slate-400 mt-0.5">
                              {lastStatus ? format(lastStatus.timestamp.toDate(), 'h:mm a') : ''}
                            </p>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Register: Attendance Logs Database Table */}
          <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-50 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-black text-slate-900 tracking-tight">Attendance Register</h3>
                  <p className="text-xs text-slate-500 font-medium">Full database of employee clocking history</p>
                </div>
                
                {/* Export Options */}
                <div className="flex items-center gap-2 flex-wrap">
                  <button 
                    onClick={() => {
                      const filtered = attendanceLogs.filter(log => {
                        const matchesSearch = log.employeeName.toLowerCase().includes(attendanceSearchTerm.toLowerCase()) ||
                                             log.department.toLowerCase().includes(attendanceSearchTerm.toLowerCase());
                        const matchesType = attendanceFilterType === 'all' || log.type === attendanceFilterType;
                        const matchesDate = !attendanceFilterDate || format(log.timestamp.toDate(), 'yyyy-MM-dd') === attendanceFilterDate;
                        return matchesSearch && matchesType && matchesDate;
                      });

                      const csvContent = [
                        ['Timestamp', 'Employee Name', 'Employee Code', 'Department', 'Type', 'Latitude', 'Longitude', 'Location Error', 'Note', 'Device Info'].join(','),
                        ...filtered.map(log => {
                          const timestamp = format(log.timestamp.toDate(), 'yyyy-MM-dd HH:mm:ss');
                          const escapedName = `"${log.employeeName.replace(/"/g, '""')}"`;
                          const code = log.employeeCode || '';
                          const department = log.department || '';
                          const type = log.type;
                          const lat = log.latitude || '';
                          const lng = log.longitude || '';
                          const err = log.locationError || '';
                          const escapedNote = `"${(log.note || '').replace(/"/g, '""')}"`;
                          const escapedDevice = `"${(log.deviceInfo || '').replace(/"/g, '""')}"`;
                          return [timestamp, escapedName, code, department, type, lat, lng, err, escapedNote, escapedDevice].join(',');
                        })
                      ].join('\n');
                      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                      downloadFile(blob, `Attendance_Register_${format(new Date(), 'yyyy-MM-dd')}.csv`);
                      toast.success('Attendance CSV exported successfully');
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
                  >
                    <Download className="w-4 h-4" /> CSV Export
                  </button>
                  <button 
                    onClick={() => {
                      const doc = new jsPDF('landscape');
                      
                      // Cover Blue Header Block
                      doc.setFillColor(30, 64, 175);
                      doc.rect(0, 0, 297, 30, 'F');
                      
                      doc.setTextColor(255, 255, 255);
                      doc.setFontSize(18);
                      doc.setFont('helvetica', 'bold');
                      doc.text('BRIGHT TECHNICAL SYSTEMS', 148, 12, { align: 'center' });
                      doc.setFontSize(10);
                      doc.setFont('helvetica', 'normal');
                      doc.text('EMPLOYEE ATTENDANCE REGISTER - ALL LOGS', 148, 19, { align: 'center' });
                      doc.setFontSize(8);
                      doc.text(`Report Generated At: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')} • Filtered Logs`, 148, 25, { align: 'center' });
                      
                      const filteredList = attendanceLogs.filter(log => {
                        const matchesSearch = log.employeeName.toLowerCase().includes(attendanceSearchTerm.toLowerCase()) ||
                                             log.department.toLowerCase().includes(attendanceSearchTerm.toLowerCase());
                        const matchesType = attendanceFilterType === 'all' || log.type === attendanceFilterType;
                        const matchesDate = !attendanceFilterDate || format(log.timestamp.toDate(), 'yyyy-MM-dd') === attendanceFilterDate;
                        return matchesSearch && matchesType && matchesDate;
                      });

                      const tableRows = filteredList.map((log) => [
                        format(log.timestamp.toDate(), 'yyyy-MM-dd HH:mm:ss'),
                        log.employeeName,
                        log.employeeCode || 'N/A',
                        log.department,
                        log.type.toUpperCase(),
                        log.latitude && log.longitude ? `${log.latitude.toFixed(5)}, ${log.longitude.toFixed(5)}` : (log.locationError || 'No GPS'),
                        log.note || 'N/A'
                      ]);
                      
                      autoTable(doc, {
                        startY: 38,
                        head: [['Timestamp', 'Employee Name', 'Code', 'Department', 'Type', 'Location (Lat, Lng)', 'Note/Comment']],
                        body: tableRows,
                        theme: 'striped',
                        headStyles: { fillColor: [30, 64, 175] },
                        styles: { fontSize: 8 },
                      });
                      
                      doc.save(`Attendance_Logs_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
                      toast.success('Attendance PDF exported successfully');
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-800 hover:bg-blue-900 text-white rounded-xl text-xs font-bold transition-all cursor-pointer"
                  >
                    <Download className="w-4 h-4" /> PDF Report
                  </button>
                </div>
              </div>

              {/* Filters Row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input 
                    type="text" 
                    placeholder="Search name or department..." 
                    value={attendanceSearchTerm}
                    onChange={(e) => setAttendanceSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                  />
                </div>
                <div>
                  <select 
                    value={attendanceFilterType} 
                    onChange={(e: any) => setAttendanceFilterType(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                  >
                    <option value="all">All Logs (Check-in & Out)</option>
                    <option value="check-in">Check-Ins only</option>
                    <option value="check-out">Check-Outs only</option>
                  </select>
                </div>
                <div>
                  <input 
                    type="date" 
                    value={attendanceFilterDate}
                    onChange={(e) => setAttendanceFilterDate(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Table rendering of filtered logs */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left bg-slate-50/50">
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Timestamp</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Employee Details</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Action</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Captured Location</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Notes / Comments</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(() => {
                    const filtered = attendanceLogs.filter(log => {
                      const matchesSearch = log.employeeName.toLowerCase().includes(attendanceSearchTerm.toLowerCase()) ||
                                           log.department.toLowerCase().includes(attendanceSearchTerm.toLowerCase());
                      const matchesType = attendanceFilterType === 'all' || log.type === attendanceFilterType;
                      const matchesDate = !attendanceFilterDate || format(log.timestamp.toDate(), 'yyyy-MM-dd') === attendanceFilterDate;
                      return matchesSearch && matchesType && matchesDate;
                    });

                    if (filtered.length === 0) {
                      return (
                        <tr>
                          <td colSpan={5} className="text-center py-12 text-sm text-slate-400 font-medium">
                            No matching attendance logs found in the database.
                          </td>
                        </tr>
                      );
                    }

                    return filtered.map((log) => (
                      <tr key={log.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs font-bold text-slate-900">
                            {format(log.timestamp.toDate(), 'dd MMM yyyy')}
                          </div>
                          <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                            {format(log.timestamp.toDate(), 'hh:mm:ss a')}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-50 text-blue-800 rounded-lg flex items-center justify-center text-xs font-bold">
                              {log.employeeName[0]}
                            </div>
                            <div>
                              <p className="text-xs font-bold text-slate-900">{log.employeeName}</p>
                              <p className="text-[9px] text-slate-500">{log.position} • {log.department}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {log.type === 'check-in' ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100/50 font-sans">
                              <LogIn className="w-3 h-3" /> IN
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-orange-50 text-orange-600 border border-orange-100/50 font-sans">
                              <LogOut className="w-3 h-3" /> OUT
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {log.latitude && log.longitude ? (
                            <a 
                              href={`https://www.google.com/maps/search/?api=1&query=${log.latitude},${log.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-800 hover:text-blue-900 font-bold hover:underline"
                            >
                              <MapPin className="w-3.5 h-3.5" />
                              {log.latitude.toFixed(5)}, {log.longitude.toFixed(5)}
                            </a>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 font-medium italic">
                              <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
                              {log.locationError || 'No GPS Data'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-xs text-slate-600 font-medium max-w-xs truncate" title={log.note || ''}>
                          {log.note || <span className="text-slate-300 italic">None</span>}
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <AnimatePresence>
        {isPayrollModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsPayrollModalOpen(false)} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="relative w-full max-w-6xl bg-white rounded-[2.5rem] shadow-2xl p-8 overflow-y-auto max-h-[95vh]">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                <div>
                  <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                    <Calculator className="w-6 h-6 text-teal-700" /> Batch Payroll Processing
                  </h2>
                  <p className="text-sm text-slate-500 font-medium">Enter multiple employees — each with individual allowances and deductions. Results include salary slips, Excel & PDF exports.</p>
                </div>
                <button onClick={() => setIsPayrollModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors"><X className="w-6 h-6 text-slate-400" /></button>
              </div>

              {showPayrollPreview ? (
                <div className="space-y-6">
                  <div className="bg-teal-50 border border-teal-200 p-4 rounded-2xl flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-black text-teal-900">Payroll Calculation Preview ({payrollForm.month})</h3>
                      <p className="text-xs text-teal-700 font-medium">Review computed salaries, deductions, and net payouts for {payrollForm.employeeIds.length} selected employee(s) before final commitment.</p>
                    </div>
                    <button 
                      type="button" 
                      onClick={() => setShowPayrollPreview(false)} 
                      className="px-4 py-2 bg-white text-teal-800 text-xs font-bold rounded-xl border border-teal-200 hover:bg-teal-100 transition-all shadow-sm"
                    >
                      Back to Edit Selection
                    </button>
                  </div>

                  {(() => {
                    let totalGross = 0;
                    let totalDeductions = 0;
                    let totalNet = 0;
                    const previewRows = payrollForm.employeeIds.map(empId => {
                      const emp = employees.find(e => e.id === empId);
                      if (!emp) return null;
                      const allowances = (emp.allowances || []).reduce((s, a) => s + a.amount, 0);
                      const gross = emp.salary + allowances;
                      const statutoryOpt = payrollForm.employeeStatutoryOpt?.[empId] || { nssf: true, nhif: true, paye: true, heslb: true };
                      const nssf = statutoryOpt.nssf ? emp.salary * 0.10 : 0;
                      const isExempt = payrollForm.employeeTaxExempt?.[empId] ?? (emp.isTaxExempt || false);
                      const residency = payrollForm.employeeTaxResidency?.[empId] || emp.taxResidency || 'resident';
                      const taxable = (isExempt || !statutoryOpt.paye) ? 0 : Math.max(0, gross - nssf);
                      const paye = (statutoryOpt.paye && !isExempt) ? calculateTanzaniaPAYE(taxable, residency, isExempt) : 0;
                      const nhif = statutoryOpt.nhif ? gross * 0.03 : 0;
                      const heslb = (statutoryOpt.heslb && emp.hasHeslbLoan) ? emp.salary * 0.15 : 0;
                      
                      const assignedCatIds = payrollForm.employeeCustomDeductions?.[empId] || [];
                      let otherDed = 0;
                      assignedCatIds.forEach(catId => {
                        const cat = deductionCategories.find(c => c.id === catId);
                        if (cat) {
                          otherDed += cat.type === 'percentage' ? ((emp.salary || 0) * (cat.value || 0)) / 100 : (cat.value || 0);
                        }
                      });

                      const deductions = nssf + paye + nhif + heslb + otherDed;
                      const net = gross - deductions;

                      totalGross += gross;
                      totalDeductions += deductions;
                      totalNet += net;

                      return { emp, gross, deductions, net, nssf, paye, nhif, heslb };
                    }).filter(Boolean);

                    return (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl">
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Gross Pay</span>
                            <p className="text-xl font-black text-slate-900 mt-1">TZS {totalGross.toLocaleString()}</p>
                          </div>
                          <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl">
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Deductions</span>
                            <p className="text-xl font-black text-rose-600 mt-1">TZS {totalDeductions.toLocaleString()}</p>
                          </div>
                          <div className="bg-teal-50 border border-teal-200 p-4 rounded-2xl">
                            <span className="text-xs font-bold text-teal-700 uppercase tracking-wider">Total Net Payout</span>
                            <p className="text-xl font-black text-teal-900 mt-1">TZS {totalNet.toLocaleString()}</p>
                          </div>
                        </div>

                        <div className="border border-slate-200 rounded-2xl overflow-hidden">
                          <div className="bg-slate-900 text-white px-4 py-3 text-xs font-bold uppercase tracking-widest">
                            Detailed Employee Summary ({previewRows.length} Staff)
                          </div>
                          <div className="overflow-x-auto max-h-[300px]">
                            <table className="w-full text-left border-collapse text-xs">
                              <thead className="bg-slate-100 text-slate-600 sticky top-0 font-bold uppercase text-[10px]">
                                <tr>
                                  <th className="p-3">Employee Name</th>
                                  <th className="p-3 text-right">Basic Salary</th>
                                  <th className="p-3 text-right">Gross Pay</th>
                                  <th className="p-3 text-right">NSSF (10%)</th>
                                  <th className="p-3 text-right">PAYE</th>
                                  <th className="p-3 text-right">Total Deductions</th>
                                  <th className="p-3 text-right font-black text-slate-900">Net Salary</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 font-medium">
                                {previewRows.map((r, i) => r && (
                                  <tr key={r.emp.id || i} className="hover:bg-slate-50 transition-colors">
                                    <td className="p-3 font-bold text-slate-900">
                                      {r.emp.firstName} {r.emp.lastName}
                                      <span className="block text-[10px] text-slate-400 font-mono">{r.emp.employeeCode || r.emp.department}</span>
                                    </td>
                                    <td className="p-3 text-right text-slate-700">TZS {r.emp.salary.toLocaleString()}</td>
                                    <td className="p-3 text-right font-bold text-slate-800">TZS {r.gross.toLocaleString()}</td>
                                    <td className="p-3 text-right text-slate-600">TZS {r.nssf.toLocaleString()}</td>
                                    <td className="p-3 text-right text-slate-600">TZS {r.paye.toLocaleString()}</td>
                                    <td className="p-3 text-right text-rose-600 font-semibold">TZS {r.deductions.toLocaleString()}</td>
                                    <td className="p-3 text-right font-black text-teal-800">TZS {r.net.toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    );
                  })()}

                  <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => setShowPayrollPreview(false)}
                      className="px-6 py-3 bg-slate-100 text-slate-700 rounded-2xl font-bold hover:bg-slate-200 transition-all text-sm"
                    >
                      Back to Edit
                    </button>
                    <button
                      type="button"
                      onClick={handlePayrollSubmit}
                      disabled={isSubmitting}
                      className="px-8 py-3 bg-teal-700 hover:bg-teal-800 text-white rounded-2xl font-bold transition-all shadow-lg shadow-teal-700/20 disabled:opacity-50 flex items-center gap-2 text-sm"
                    >
                      {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                      Confirm & Process Payroll ({payrollForm.employeeIds.length} Staff)
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handlePayrollSubmit} className="space-y-6">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <div className="space-y-1 w-full md:w-auto">
                    <label className="text-xs font-bold text-slate-700">Payroll Month *</label>
                    <input required type="month" value={payrollForm.month} onChange={(e) => setPayrollForm({ ...payrollForm, month: e.target.value })} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800" />
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setPayrollForm({ ...payrollForm, employeeIds: employees.map(e => e.id) })} className="px-4 py-2 bg-teal-50 text-teal-800 text-xs font-bold rounded-xl hover:bg-teal-100 transition-all">Select All Registered Staff</button>
                    <button type="button" onClick={() => setPayrollForm({ ...payrollForm, employeeIds: [] })} className="px-4 py-2 bg-slate-200 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-300 transition-all">Clear All</button>
                  </div>
                </div>

                <div className="border border-slate-200 rounded-2xl overflow-hidden">
                  <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between text-xs font-bold uppercase tracking-widest">
                    <span>Employee Data ({employees.length} Registered Staff Available)</span>
                    <span className="text-[10px] text-teal-400">Amounts in TZS | NSSF (10%) & PSSF (5%)</span>
                  </div>
                  <div className="overflow-x-auto max-h-[350px]">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead className="bg-slate-100 text-slate-600 sticky top-0 font-bold uppercase text-[10px]">
                        <tr>
                          <th className="p-3">#</th>
                          <th className="p-3">Employee Name *</th>
                          <th className="p-3 text-right">Basic Salary</th>
                          <th className="p-3 text-right">Transport Allow.</th>
                          <th className="p-3 text-right">Housing Allow.</th>
                          <th className="p-3 text-right">Meal Allow.</th>
                          <th className="p-3 text-right">Sector</th>
                          <th className="p-3 text-right">Employment Type</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium">
                        {employees.map((emp, idx) => {
                          const isSelected = payrollForm.employeeIds.includes(emp.id);
                          return (
                            <React.Fragment key={emp.id}>
                              <tr className={cn("hover:bg-teal-50/25 transition-colors", isSelected ? "bg-teal-50/40" : "")}>
                                <td className="p-3">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      const newIds = e.target.checked
                                        ? [...payrollForm.employeeIds, emp.id]
                                        : payrollForm.employeeIds.filter(id => id !== emp.id);
                                      setPayrollForm({ ...payrollForm, employeeIds: newIds });
                                    }}
                                    className="w-4 h-4 rounded border-slate-300 text-teal-700 focus:ring-teal-700"
                                  />
                                </td>
                                <td className="p-3 font-bold text-slate-900">
                                  {emp.firstName} {emp.lastName}
                                  <span className="block text-[10px] text-slate-400 font-mono">{emp.employeeCode || emp.department}</span>
                                </td>
                                <td className="p-3 text-right font-bold text-slate-800">
                                  {(emp.salary || 1200000).toLocaleString()}
                                </td>
                                <td className="p-3 text-right text-slate-600">100,000</td>
                                <td className="p-3 text-right text-slate-600">100,000</td>
                                <td className="p-3 text-right text-slate-600">50,000</td>
                                <td className="p-3 text-right">
                                  <span className="px-2 py-1 bg-teal-100 text-teal-800 rounded font-bold text-[10px]">Private</span>
                                </td>
                                <td className="p-3 text-right">
                                  <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded font-bold text-[10px]">Primary</span>
                                </td>
                              </tr>
                              {isSelected && (
                                <tr className="bg-teal-50/60">
                                  <td colSpan={8} className="p-4">
                                    <div className="bg-white p-4 rounded-xl border border-teal-200 shadow-sm space-y-4">
                                      <div className="font-black text-teal-900 text-xs uppercase tracking-wider flex items-center justify-between">
                                        <span>Custom Deductions & Tax Settings for {emp.firstName} {emp.lastName}</span>
                                        <span className="text-[10px] bg-teal-100 text-teal-800 px-2 py-0.5 rounded">Mark applicable deductions</span>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
                                        <div>
                                          <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Tax Residency</label>
                                          <select
                                            value={payrollForm.employeeTaxResidency?.[emp.id] || emp.taxResidency || 'resident'}
                                            onChange={(e) => {
                                              setPayrollForm({
                                                ...payrollForm,
                                                employeeTaxResidency: {
                                                  ...(payrollForm.employeeTaxResidency || {}),
                                                  [emp.id]: e.target.value as any
                                                }
                                              });
                                            }}
                                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-800"
                                          >
                                            <option value="resident">Resident</option>
                                            <option value="non-resident">Non-Resident (15%)</option>
                                          </select>
                                          <div className="mt-3">
                                            <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700">
                                              <input
                                                type="checkbox"
                                                checked={payrollForm.employeeTaxExempt?.[emp.id] ?? (emp.isTaxExempt || false)}
                                                onChange={(e) => {
                                                  setPayrollForm({
                                                    ...payrollForm,
                                                    employeeTaxExempt: {
                                                      ...(payrollForm.employeeTaxExempt || {}),
                                                      [emp.id]: e.target.checked
                                                    }
                                                  });
                                                }}
                                                className="w-4 h-4 rounded border-slate-300 text-teal-700"
                                              />
                                              Tax Exempt (0 PAYE)
                                            </label>
                                          </div>
                                        </div>

                                        {/* Statutory Deductions */}
                                        <div>
                                          <span className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Statutory Deductions</span>
                                          <div className="space-y-1.5 bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                                            {['nssf', 'nhif', 'paye', 'heslb'].map(statKey => {
                                              const statOpt = payrollForm.employeeStatutoryOpt?.[emp.id] ?? { nssf: true, nhif: true, paye: true, heslb: true };
                                              const isChecked = (statOpt as any)[statKey];
                                              const labels: Record<string, string> = {
                                                nssf: 'NSSF (10% Pension)',
                                                nhif: 'NHIF (3% Health)',
                                                paye: 'PAYE (Income Tax)',
                                                heslb: 'HESLB (Loan 15%)'
                                              };
                                              return (
                                                <label key={statKey} className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer hover:bg-slate-100 p-1 rounded">
                                                  <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                      const currentOpt = payrollForm.employeeStatutoryOpt?.[emp.id] ?? { nssf: true, nhif: true, paye: true, heslb: true };
                                                      setPayrollForm({
                                                        ...payrollForm,
                                                        employeeStatutoryOpt: {
                                                          ...(payrollForm.employeeStatutoryOpt || {}),
                                                          [emp.id]: {
                                                            ...currentOpt,
                                                            [statKey]: e.target.checked
                                                          }
                                                        }
                                                      });
                                                    }}
                                                    className="w-3.5 h-3.5 rounded border-slate-300 text-teal-700"
                                                  />
                                                  <span>{labels[statKey]}</span>
                                                </label>
                                              );
                                            })}
                                          </div>
                                        </div>

                                        {/* Custom Deductions / Loans */}
                                        <div className="md:col-span-2">
                                          <span className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Custom Deductions / Loans (Mark all that apply)</span>
                                          <div className="max-h-36 overflow-y-auto space-y-1 bg-slate-50 p-2 rounded-lg border border-slate-200">
                                            {deductionCategories.length === 0 ? (
                                              <span className="text-[10px] text-slate-400 italic">No custom deductions created in Deductions & Loans tab</span>
                                            ) : (
                                              deductionCategories.map(cat => {
                                                const assignedList = payrollForm.employeeCustomDeductions?.[emp.id] || [];
                                                const isChecked = assignedList.includes(cat.id);
                                                return (
                                                  <label key={cat.id} className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer hover:bg-slate-100 p-1 rounded">
                                                    <input
                                                      type="checkbox"
                                                      checked={isChecked}
                                                      onChange={(e) => {
                                                        const current = payrollForm.employeeCustomDeductions?.[emp.id] || [];
                                                        const updated = e.target.checked
                                                          ? [...current, cat.id]
                                                          : current.filter(id => id !== cat.id);
                                                        setPayrollForm({
                                                          ...payrollForm,
                                                          employeeCustomDeductions: {
                                                            ...(payrollForm.employeeCustomDeductions || {}),
                                                            [emp.id]: updated
                                                          }
                                                        });
                                                        const existingP = payrolls.find(p => p.employeeId === emp.id && p.month === payrollForm.month);
                                                        if (existingP) {
                                                          updateEmployeePayrollMarkedDeductions(existingP.id, updated).catch(err => console.error(err));
                                                        }
                                                      }}
                                                      className="w-3.5 h-3.5 rounded border-slate-300 text-teal-700"
                                                    />
                                                    <span>{cat.name} ({cat.type === 'percentage' ? `${cat.value}%` : `TZS ${cat.value?.toLocaleString()}`})</span>
                                                  </label>
                                                );
                                              })
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div className="text-xs text-slate-500 font-medium">
                    {payrollForm.employeeIds.length} of {employees.length} employees selected for payroll generation.
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting || payrollForm.employeeIds.length === 0}
                    className="px-8 py-4 bg-teal-700 hover:bg-teal-800 text-white rounded-2xl font-bold transition-all shadow-lg shadow-teal-700/20 disabled:opacity-50 flex items-center gap-2 text-sm"
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Calculator className="w-5 h-5" />}
                    Preview Payroll ({payrollForm.employeeIds.length} Staff)
                  </button>
                </div>
              </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MCColn Salary Calculator Modal */}
      <AnimatePresence>
        {isMcColnCalculatorOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsMcColnCalculatorOpen(false)} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="relative w-full max-w-5xl bg-white rounded-[2.5rem] shadow-2xl p-8 overflow-y-auto max-h-[90vh]">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <Calculator className="w-6 h-6 text-blue-800" /> MCColn Tanzania Batch Salary Calculator & Payroll Hub
                  </h2>
                  <p className="text-sm text-slate-500 font-medium">Full TRA, NSSF, NHIF, HESLB, SDL & WCF batch simulation and bulk generation (mccoln.com/batch)</p>
                </div>
                <button onClick={() => setIsMcColnCalculatorOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors"><X className="w-6 h-6 text-slate-400" /></button>
              </div>

              <div className="space-y-6">
                {/* Batch Summary Cards */}
                {(() => {
                  const batchCalculations = employees.map(emp => {
                    const gross = (emp.salary || 0) + 200000; // default allowances
                    const nssfEmp = gross * 0.10;
                    const taxable = Math.max(0, gross - nssfEmp);
                    const payeVal = calculateTanzaniaPAYE(taxable, emp.taxResidency || 'resident', emp.isTaxExempt || false);
                    const nhifEmp = gross * 0.03;
                    const heslbVal = emp.hasHeslbLoan ? (emp.salary || 0) * 0.15 : 0;
                    const totalDed = nssfEmp + payeVal + nhifEmp + heslbVal;
                    const net = gross - totalDed;
                    const nssfEmpr = gross * 0.10;
                    const sdlVal = gross * 0.035;
                    const wcfVal = gross * 0.01;
                    const totalEmprCost = gross + nssfEmpr + sdlVal + wcfVal;
                    return { emp, gross, nssfEmp, payeVal, nhifEmp, heslbVal, net, nssfEmpr, sdlVal, wcfVal, totalEmprCost };
                  });

                  const totalGross = batchCalculations.reduce((acc, x) => acc + x.gross, 0);
                  const totalNet = batchCalculations.reduce((acc, x) => acc + x.net, 0);
                  const totalTax = batchCalculations.reduce((acc, x) => acc + x.payeVal, 0);
                  const totalNssf = batchCalculations.reduce((acc, x) => acc + (x.nssfEmp + x.nssfEmpr), 0);
                  const totalEmprCost = batchCalculations.reduce((acc, x) => acc + x.totalEmprCost, 0);

                  return (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                        <p className="text-[10px] font-black uppercase text-slate-400">Total Workforce</p>
                        <h4 className="text-lg font-black text-slate-900 mt-0.5">{employees.length} Staff</h4>
                      </div>
                      <div className="bg-blue-50/60 p-4 rounded-2xl border border-blue-100">
                        <p className="text-[10px] font-black uppercase text-blue-600">Total Gross Pay</p>
                        <h4 className="text-lg font-black text-blue-900 mt-0.5">TZS {totalGross.toLocaleString()}</h4>
                      </div>
                      <div className="bg-emerald-50 p-4 rounded-2xl border border-emerald-100">
                        <p className="text-[10px] font-black uppercase text-emerald-600">Total Net Take-Home</p>
                        <h4 className="text-lg font-black text-emerald-900 mt-0.5">TZS {totalNet.toLocaleString()}</h4>
                      </div>
                      <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100">
                        <p className="text-[10px] font-black uppercase text-amber-600">Total PAYE Tax</p>
                        <h4 className="text-lg font-black text-amber-900 mt-0.5">TZS {totalTax.toLocaleString()}</h4>
                      </div>
                      <div className="bg-indigo-50 p-4 rounded-2xl border border-indigo-100 col-span-2 md:col-span-1">
                        <p className="text-[10px] font-black uppercase text-indigo-600">Total Employer Cost</p>
                        <h4 className="text-lg font-black text-indigo-900 mt-0.5">TZS {totalEmprCost.toLocaleString()}</h4>
                      </div>
                    </div>
                  );
                })()}

                {/* Batch Table */}
                <div className="border border-slate-200 rounded-2xl overflow-hidden">
                  <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-sm">MCColn Batch Employee Payroll Breakdown</h3>
                      <p className="text-[11px] text-slate-400">Real-time Tanzania statutory computation per employee</p>
                    </div>
                    <button
                      onClick={async () => {
                        await handleAsyncAction(async () => {
                          for (const employee of employees) {
                            const allowancesAmount = 200000;
                            const grossPay = (employee.salary || 0) + allowancesAmount;
                            const nssfEmployee = grossPay * 0.10;
                            const taxablePay = Math.max(0, grossPay - nssfEmployee);
                            const paye = calculateTanzaniaPAYE(taxablePay, employee.taxResidency || 'resident', employee.isTaxExempt || false);
                            const nhifEmployee = grossPay * 0.03;
                            const heslb = employee.hasHeslbLoan ? (employee.salary || 0) * 0.15 : 0;
                            const nssfEmployer = grossPay * 0.10;
                            const nhifEmployer = 0;
                            const sdl = grossPay * 0.035;
                            const wcf = grossPay * 0.01;
                            const totalDeductionsEmployee = nssfEmployee + paye + nhifEmployee + heslb;
                            const netSalary = grossPay - totalDeductionsEmployee;

                            const data: Omit<Payroll, 'id'> = {
                              employeeId: employee.id || '',
                              employeeCode: employee.employeeCode || employee.id?.substring(0, 8).toUpperCase() || '',
                              employeeName: `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Employee',
                              employeeEmail: employee.email && isValidEmail(employee.email) ? employee.email : '',
                              department: employee.department || 'General',
                              month: format(new Date(), 'yyyy-MM'),
                              basicSalary: employee.salary || 0,
                              allowancesAmount,
                              grossPay,
                              taxablePay,
                              nssfEmployee,
                              paye,
                              nhifEmployee,
                              heslb,
                              nssfEmployer,
                              nhifEmployer,
                              sdl,
                              wcf,
                              deductions: [],
                              otherDeductionsAmount: 0,
                              totalDeductions: totalDeductionsEmployee,
                              netSalary,
                              status: 'pending',
                              createdAt: Timestamp.now()
                            };
                            await addDoc(collection(db, 'payrolls'), data);
                          }
                          setIsMcColnCalculatorOpen(false);
                        }, setIsSubmitting, {
                          timeoutMs: 0,
                          successMessage: 'Batch payroll generated successfully for all employees!',
                          errorMessage: 'Failed to generate batch payroll',
                        });
                      }}
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl transition-all shadow-md flex items-center gap-2"
                    >
                      {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Generate All to System
                    </button>
                  </div>
                  <div className="overflow-x-auto max-h-[350px]">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-slate-50 text-[10px] uppercase font-black text-slate-500 sticky top-0 border-b border-slate-200">
                        <tr>
                          <th className="p-3">Employee</th>
                          <th className="p-3 text-right">Basic (TZS)</th>
                          <th className="p-3 text-right">Gross (TZS)</th>
                          <th className="p-3 text-right">NSSF (10%)</th>
                          <th className="p-3 text-right">PAYE</th>
                          <th className="p-3 text-right">NHIF (3%)</th>
                          <th className="p-3 text-right">Net Take-Home</th>
                          <th className="p-3 text-right">Employer Cost</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-xs font-medium text-slate-700">
                        {employees.map(emp => {
                          const gross = (emp.salary || 0) + 200000;
                          const nssfEmp = gross * 0.10;
                          const taxable = Math.max(0, gross - nssfEmp);
                          const payeVal = calculateTanzaniaPAYE(taxable, emp.taxResidency || 'resident', emp.isTaxExempt || false);
                          const nhifEmp = gross * 0.03;
                          const heslbVal = emp.hasHeslbLoan ? (emp.salary || 0) * 0.15 : 0;
                          const totalDed = nssfEmp + payeVal + nhifEmp + heslbVal;
                          const net = gross - totalDed;
                          const nssfEmpr = gross * 0.10;
                          const sdlVal = gross * 0.035;
                          const wcfVal = gross * 0.01;
                          const totalEmprCost = gross + nssfEmpr + sdlVal + wcfVal;

                          return (
                            <tr key={emp.id} className="hover:bg-slate-50/80">
                              <td className="p-3 font-bold text-slate-900">
                                {emp.firstName} {emp.lastName}
                                <span className="block text-[10px] text-slate-400 font-mono">{emp.employeeCode || emp.department}</span>
                              </td>
                              <td className="p-3 text-right">{(emp.salary || 0).toLocaleString()}</td>
                              <td className="p-3 text-right font-bold text-blue-900">{gross.toLocaleString()}</td>
                              <td className="p-3 text-right text-slate-600">{nssfEmp.toLocaleString()}</td>
                              <td className="p-3 text-right text-red-600">{payeVal.toLocaleString()}</td>
                              <td className="p-3 text-right text-slate-600">{nhifEmp.toLocaleString()}</td>
                              <td className="p-3 text-right font-black text-emerald-600">{net.toLocaleString()}</td>
                              <td className="p-3 text-right font-bold text-indigo-900">{totalEmprCost.toLocaleString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button onClick={() => setIsMcColnCalculatorOpen(false)} className="px-6 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all shadow-md">Close Calculator</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
        {/* Import Employees Excel Preview Modal */}
        {isImportEmployeeModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !isImportingEmployees && setIsImportEmployeeModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-5xl bg-white rounded-[2.5rem] shadow-2xl p-6 md:p-8 max-h-[90vh] flex flex-col overflow-hidden z-10"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between pb-4 border-b border-slate-100 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-50 text-emerald-700 rounded-2xl flex items-center justify-center font-bold">
                    <FileSpreadsheet className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 tracking-tight">Import Employees from Excel Template</h2>
                    <p className="text-xs text-slate-500 font-medium">
                      Review and verify parsed staff details before importing into HR Register.
                    </p>
                  </div>
                </div>
                <button
                  disabled={isImportingEmployees}
                  onClick={() => setIsImportEmployeeModalOpen(false)}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Summary Chips */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 py-4 border-b border-slate-100 shrink-0 text-center">
                <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100">
                  <p className="text-[10px] font-black uppercase text-slate-400">Total Rows</p>
                  <p className="text-lg font-black text-slate-900">{importedEmployeeRows.length}</p>
                </div>
                <div className="bg-emerald-50 p-3 rounded-2xl border border-emerald-100">
                  <p className="text-[10px] font-black uppercase text-emerald-700">Selected Valid</p>
                  <p className="text-lg font-black text-emerald-900">
                    {importedEmployeeRows.filter(r => r.selected && r.validationErrors.length === 0).length}
                  </p>
                </div>
                <div className="bg-blue-50 p-3 rounded-2xl border border-blue-100">
                  <p className="text-[10px] font-black uppercase text-blue-700">Updates Existing</p>
                  <p className="text-lg font-black text-blue-900">
                    {importedEmployeeRows.filter(r => employees.some(e => e.email.toLowerCase() === r.email.toLowerCase())).length}
                  </p>
                </div>
                <div className="bg-amber-50 p-3 rounded-2xl border border-amber-100">
                  <p className="text-[10px] font-black uppercase text-amber-700">Validation Errors</p>
                  <p className="text-lg font-black text-amber-900">
                    {importedEmployeeRows.filter(r => r.validationErrors.length > 0).length}
                  </p>
                </div>
              </div>

              {/* Select / Deselect All Controls */}
              <div className="flex items-center justify-between pt-3 pb-2 text-xs shrink-0">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setImportedEmployeeRows(prev => prev.map(r => ({ ...r, selected: r.validationErrors.length === 0 })))}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors"
                  >
                    Select All Valid
                  </button>
                  <button
                    type="button"
                    onClick={() => setImportedEmployeeRows(prev => prev.map(r => ({ ...r, selected: false })))}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors"
                  >
                    Deselect All
                  </button>
                </div>
                <span className="text-slate-400 font-medium italic">
                  File: <span className="font-bold text-slate-700">{employeeExcelFileName}</span>
                </span>
              </div>

              {/* Table Preview */}
              <div className="flex-1 overflow-y-auto border border-slate-100 rounded-2xl my-2">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-black uppercase tracking-wider text-slate-400 sticky top-0 bg-slate-50 z-10">
                      <th className="p-3 w-10 text-center">
                        <input
                          type="checkbox"
                          checked={importedEmployeeRows.length > 0 && importedEmployeeRows.every(r => r.selected || r.validationErrors.length > 0)}
                          onChange={(e) => {
                            const check = e.target.checked;
                            setImportedEmployeeRows(prev => prev.map(r => ({ ...r, selected: r.validationErrors.length === 0 ? check : false })));
                          }}
                          className="rounded text-blue-800 focus:ring-blue-800"
                        />
                      </th>
                      <th className="p-3">#</th>
                      <th className="p-3">Employee Name</th>
                      <th className="p-3">Email & Phone</th>
                      <th className="p-3">Dept & Position</th>
                      <th className="p-3">Category</th>
                      <th className="p-3">Basic Salary</th>
                      <th className="p-3">Action Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs">
                    {importedEmployeeRows.map((row) => {
                      const isExisting = employees.some(e => e.email.toLowerCase() === row.email.toLowerCase());
                      return (
                        <tr
                          key={row.rowIndex}
                          className={cn(
                            "hover:bg-slate-50 transition-colors",
                            row.validationErrors.length > 0 ? "bg-amber-50/40" : row.selected ? "bg-emerald-50/20" : ""
                          )}
                        >
                          <td className="p-3 text-center">
                            <input
                              type="checkbox"
                              checked={row.selected}
                              disabled={row.validationErrors.length > 0}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setImportedEmployeeRows(prev =>
                                  prev.map(r => (r.rowIndex === row.rowIndex ? { ...r, selected: checked } : r))
                                );
                              }}
                              className="rounded text-blue-800 focus:ring-blue-800 disabled:opacity-40"
                            />
                          </td>
                          <td className="p-3 font-mono text-slate-400 text-[10px]">{row.rowIndex}</td>
                          <td className="p-3">
                            <p className="font-bold text-slate-900">{row.firstName} {row.lastName}</p>
                            {row.employeeCode && <p className="text-[10px] font-mono text-slate-400">{row.employeeCode}</p>}
                          </td>
                          <td className="p-3">
                            <p className="font-medium text-slate-700">{row.email || <span className="text-amber-600 italic">Missing Email</span>}</p>
                            <p className="text-[10px] text-slate-400">{row.phone}</p>
                          </td>
                          <td className="p-3">
                            <p className="font-bold text-slate-800">{row.department}</p>
                            <p className="text-[10px] text-slate-500">{row.position}</p>
                          </td>
                          <td className="p-3">
                            <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-slate-100 text-slate-700">
                              {row.category}
                            </span>
                          </td>
                          <td className="p-3 font-black text-slate-900">
                            TZS {row.salary.toLocaleString()}
                          </td>
                          <td className="p-3">
                            {row.validationErrors.length > 0 ? (
                              <span className="px-2.5 py-1 rounded-xl text-[10px] font-bold bg-amber-100 text-amber-800 flex items-center gap-1 w-fit">
                                <AlertCircle className="w-3 h-3 text-amber-600" />
                                {row.validationErrors.join(', ')}
                              </span>
                            ) : isExisting ? (
                              <span className="px-2.5 py-1 rounded-xl text-[10px] font-bold bg-blue-100 text-blue-800 flex items-center gap-1 w-fit">
                                <RefreshCw className="w-3 h-3 text-blue-600" />
                                Update Existing
                              </span>
                            ) : (
                              <span className="px-2.5 py-1 rounded-xl text-[10px] font-bold bg-emerald-100 text-emerald-800 flex items-center gap-1 w-fit">
                                <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                New Employee
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer Controls */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-100 shrink-0">
                <button
                  type="button"
                  disabled={isImportingEmployees}
                  onClick={() => setIsImportEmployeeModalOpen(false)}
                  className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-2xl text-xs transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isImportingEmployees || importedEmployeeRows.filter(r => r.selected && r.validationErrors.length === 0).length === 0}
                  onClick={handleProcessEmployeeImport}
                  className="px-8 py-3 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white font-bold rounded-2xl text-xs transition-all shadow-lg shadow-emerald-700/20 flex items-center gap-2 cursor-pointer"
                >
                  {isImportingEmployees ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Importing Staff...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>
                        Import {importedEmployeeRows.filter(r => r.selected && r.validationErrors.length === 0).length} Selected Employee(s)
                      </span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="YES"
        cancelLabel="NO"
        isDanger={true}
      />

      <SuccessPopup
        isOpen={!!payrollSuccessPopupMessage}
        message={payrollSuccessPopupMessage}
        onClose={() => setPayrollSuccessPopupMessage(null)}
        title="Payroll Successfully Created"
      />
    </div>
  );
};

function cn(...inputs: any[]) {
  return inputs.filter(Boolean).join(' ');
}

export default HRDashboard;
